// @liaison/web — browser client for liaisond.
//
// One WebSocket for control (SPEC §7) plus one RTCPeerConnection for audio
// (Opus 48 kHz, sendrecv, browser AEC). The client is always the WebRTC offerer.

import type {
	AgentRowKind,
	AgentState,
	ClientFrame,
	Config,
	ErrorFrame,
	Frame,
	Message,
	PlaybackClear,
	RunningJob,
	ServerFrame,
	SessionReady,
	SessionStart,
	SessionState,
	SpeechMessage,
	SuspendReason,
	ToolCall,
	ToolCallMessage,
	ToolEvent,
	ToolResultMessage,
	TranscriptContext,
	TranscriptTool,
} from "./protocol";

import { WAKE_DEFAULTS, type WakeTuning } from "./wake-ring";
import {
	TELEMETRY_LEVEL_MS,
	TELEMETRY_PLAYOUT_MS,
	TELEMETRY_TICK_MS,
	Telemetry,
	type Timer,
} from "./telemetry";

export type {
	AgentRowKind,
	AgentState,
	AgentStatus,
	ClientFrame,
	Config,
	ErrorFrame,
	Frame,
	Message,
	PlaybackClear,
	RunningJob,
	ServerFrame,
	SessionState,
	SpeechMessage,
	SuspendReason,
	TelemetryEvent,
	ToolCallMessage,
	ToolEvent,
	ToolResultMessage,
} from "./protocol";

/**
 * Wake-on-speech tuning and its defaults (`docs/wake-on-speech.md` §5), so a
 * consumer can read the numbers it is overriding instead of guessing them.
 */
export { WAKE_DEFAULTS } from "./wake-ring";
export type { WakeTuning } from "./wake-ring";

export const VERSION = "0.3.0";

/**
 * One input-level report from the render thread. `db`/`peakDb` are dBFS in
 * [-100, 0]; `floorDb` is the detector's tracked noise floor.
 *
 * `db` is RMS over the whole report interval and `peakDb` the loudest single
 * sample in it, because a bar drawn from RMS alone never shows the clip a user
 * has to be told about. All three are measured BEFORE the mute gate, which is
 * what lets a UI say "you are muted and talking" — the one moment a mute
 * indicator has something to add.
 */
export interface InputLevel {
	db: number;
	peakDb: number;
	floorDb: number;
	muted: boolean;
}

/**
 * One audio device, as a picker needs it. `label` is empty until the page holds
 * a `getUserMedia` grant — that is the browser's rule, not an omission here:
 * device labels are fingerprinting surface and are withheld until the user has
 * allowed the microphone at least once. A picker rendered before the first
 * `connect()` therefore has ids and no names, and there is nothing to do about
 * it but re-enumerate after the grant.
 */
export interface AudioDeviceInfo {
	deviceId: string;
	label: string;
	groupId: string;
}

/** `enumerateDevices()` split by kind: microphones and sinks. */
export interface AudioDevices {
	inputs: AudioDeviceInfo[];
	outputs: AudioDeviceInfo[];
}

/**
 * Server-reported session state, plus the three local-only edges.
 *
 * `"reconnecting"` is the SPEC §13 gap: the control socket died and the SDK is
 * backing off towards a new one. The session may well still be live on the node
 * with its media running, so this is not a suspend and the conversation is not
 * over; it is the one status where the server's own state is unknown.
 */
export type Status = "connecting" | "reconnecting" | SessionState | "closed";

/**
 * One transcript update. A user event with `final: false` and an empty `text`
 * withdraws the interim shown so far: the server discarded that pending text
 * (talk-over, a Finalize tail) and no final will ever replace it, so a client
 * must drop the line instead of leaving it above the next reply. Every other
 * user event replaces the line's text; assistant events are cumulative.
 *
 * A user event with `amend: true` corrects the *already final* turn named by
 * `messageId` — the STT re-recognised a word whose audio was already committed.
 * It is never a new turn: replace that turn's text in place, exactly as an
 * assistant event keyed by `messageId` replaces its line.
 */
export type TranscriptEvent =
	| {
			role: "user";
			text: string;
			final: boolean;
			lang?: string;
			/** Present on finals and on amendments; interims carry none. */
			messageId?: string;
			/** This event corrects the final turn `messageId`, it does not add one. */
			amend?: true;
	  }
	| {
			role: "assistant";
			messageId: string;
			/** Cumulative text of the turn, not a delta. */
			text: string;
			spokenChars: number;
			done: boolean;
			interrupted?: boolean;
			/** `"backend"`: speech the backend had the liaison say verbatim (SPEC §18.8). */
			source?: "backend";
			/** The backend job a spoken progress update was about. */
			jobId?: string;
	  }
	| {
			/**
			 * A note the backend added to the conversation (SPEC §18.8):
			 * `respond` asks for a turn, `note` is filed for the next one. The
			 * matching `s.history` row is already in place when it fires, and a
			 * replayed frame fires again under the same `messageId`.
			 */
			role: "context";
			messageId: string;
			text: string;
			source: "backend";
			mode: "respond" | "note";
			jobId?: string;
			/** Unix milliseconds, the server's own clock. */
			ts: number;
	  }
	| {
			/**
			 * A row the session's agent produced (SPEC §18.14): one of its
			 * messages (`kind:"message"`, always `respond`), or a status row
			 * (`kind:"status"`: `running`, `idle`, `failed: …`, `disconnected`,
			 * `reconnected; …` or a refusal). The model rewords messages; it
			 * never reads them out. Recorded and replayed like a backend note.
			 */
			role: "context";
			messageId: string;
			text: string;
			source: "agent";
			kind: AgentRowKind;
			mode: "respond" | "note";
			/** Unix milliseconds, the server's own clock. */
			ts: number;
	  }
	| {
			/**
			 * A tool job transition (SPEC §9). One event per `transcript.tool`
			 * frame, for every binding and not just this client's tools; the
			 * matching `s.history` record is already in place when it fires.
			 */
			role: "tool";
			jobId: string;
			name: string;
			event: ToolEvent;
			/** The model's arguments; `started` only. */
			args?: unknown;
			/** Terminal events only, and only one of the two. */
			result?: unknown;
			error?: string;
			/** Unix milliseconds, the server's own clock. */
			ts: number;
	  };

/**
 * One client tool (SPEC §9). `schema` matches `config.tools`; `graceMs`,
 * `timeoutMs`, `async`, and `effects` are honored, with a 10-second timeout default.
 * `url` is ignored; client tools never auto-retry, while `effects:false` still marks corrected-turn repeats safe.
 *
 * `handler` runs in the browser and its resolved value becomes the `tool`
 * message the model reads; a throw becomes the job's error. `signal` aborts
 * when the job is cancelled — server-side (`cancel_job`, timeout, session end)
 * or locally through `s.cancelJob(jobId)` — and the SDK sends no result for an
 * aborted job, because the job is already gone.
 */
export interface ToolDefinition {
	schema: Record<string, unknown>;
	handler(args: unknown, signal: AbortSignal): Promise<unknown>;
}

/**
 * A session token (SPEC §8) or a way to fetch one.
 *
 * The token is minted by the application's backend, which holds the tenant
 * secret, and it is what authorises the session: the config is either inside
 * it or pinned by its `cfgHash`, so a browser never holds the vendor keys and
 * never chooses the prompt. Pass the callback form when the token can expire
 * before the user presses connect — it runs on every attempt.
 */
export type TokenSource = string | (() => string | Promise<string>);

export interface ConnectOptions {
	/** Control endpoint, e.g. `ws://localhost:8080/v1/session`. */
	url: string;
	/**
	 * The session token (SPEC §8). Required by any node that was not started
	 * with `--no-auth`; a start without one is refused `unauthorized`.
	 */
	token?: TokenSource;
	/**
	 * The session config. Send it only when the token pins it by `cfgHash`
	 * (or when the node runs with `--no-auth` and there is no token at all):
	 * a token that carries its own config refuses a start that also sends
	 * one, with `config_mismatch`.
	 */
	config?: Config;
	/** Prior conversation to resume (SPEC §5.3); pass a previous `session.history`. */
	history?: Message[];
	/**
	 * Client tools (SPEC §9): declared to the server on `session.start` and run
	 * here off `tool.call`. A name colliding with a server or built-in tool
	 * rejects the session with `tool_conflict`.
	 */
	tools?: ToolDefinition[];
	onTranscript: (e: TranscriptEvent) => void;
	onState: (s: Status) => void;
	onError?: (e: ErrorFrame) => void;
	/** Server dropped its playback ring (barge-in); the SDK has flushed the jitter buffer. */
	onPlaybackClear?: (f: PlaybackClear) => void;
	/**
	 * The session was released (SPEC §10). Media is gone and `s.state` is
	 * `"suspended"`, but the object is alive and `s.history` is the whole
	 * conversation: `s.resume()` brings it back. `"idle"`, `"client"` and
	 * `"replaced"` wait for the user's gesture — the tap-to-resume button —
	 * while `"error"` is resumed by the SDK itself (SPEC §13: media died, no
	 * user action). `"replaced"` means a newer session of the same
	 * conversation started on the node, usually another tab: resuming here
	 * replaces that one in turn, so it is never automatic.
	 */
	onSuspended?: (reason: SuspendReason) => void;
	/**
	 * Local speech triggered this resume: the session was suspended on the
	 * idle rule, the wake detector heard the room and the SDK is bringing the
	 * session back by itself. Fires at the onset, before the handshake, so a
	 * consumer can say *why* the session came back — and `s.lastWake` says how
	 * far over its noise floor the sound was. Only ever with `wake` on.
	 */
	onWake?: () => void;
	/**
	 * Reconnect after an unexpected control-socket close (SPEC §13). Default
	 * true. Off means one dropped socket ends the session, which is what a
	 * caller driving its own retry loop wants; it also disables the automatic
	 * resume after a `reason:"error"` suspend.
	 */
	autoReconnect?: boolean;
	/**
	 * Wake on speech (`docs/wake-on-speech.md`), off by default. On, the SDK
	 * keeps the microphone capture and a detector alive through an **idle**
	 * suspend and resumes the session by itself when the user speaks, holding
	 * `preRollMs` of audio so the first word survives the handshake. `true`
	 * takes every default; an object overrides field by field.
	 *
	 * Two costs, both in `docs/sdk-web.md` §7 and neither negotiable: a
	 * suspended session is **still capturing**, so the browser's recording
	 * indicator stays lit on a session your UI calls paused, and the idle
	 * window this undoes belongs to whoever mints the token, not to you.
	 */
	wake?: boolean | Partial<WakeTuning>;
	/**
	 * Join muted, and stay muted until `unmute()`. Default false.
	 *
	 * Mute is local and silent: no frame goes on the wire, the server is simply
	 * handed nothing to hear. It survives everything the session does to itself
	 * — a suspend, a resume, a §13 reconnect, an ICE restart, a device switch —
	 * because it is a property of the session and not of whatever track happens
	 * to be carrying it.
	 */
	muted?: boolean;
	/**
	 * Input level, roughly every `levelIntervalMs`. Passing this callback is
	 * what turns monitoring on; there is no separate flag, because the cost of
	 * monitoring is exactly the cost of having somewhere to report to.
	 *
	 * That cost is real and worth knowing: a session that asked for neither
	 * `wake` nor this sends the raw capture track straight into the
	 * PeerConnection, and monitoring puts an `AudioContext` and one worklet hop
	 * (128 samples, 2.67 ms at 48 kHz) into the send path to get the samples.
	 * A meter is worth that; a session that does not draw one should not pay it.
	 *
	 * The report is also on `s.inputLevel`, and a meter SHOULD read it from
	 * `requestAnimationFrame` rather than re-render per callback: these arrive
	 * on the cadence the audio thread reports at, which is nobody's frame rate.
	 */
	onLevel?: (l: InputLevel) => void;
	/** Report cadence for `onLevel`, in milliseconds. Default 100, clamped to [20, 1000]. */
	levelIntervalMs?: number;
	/**
	 * Capture device (`AudioDeviceInfo.deviceId`) for the first and every later
	 * media leg. Omitted or null means the system default, which is what a
	 * session should want until a user says otherwise — the browser's default
	 * follows the OS, and the OS follows the headset being plugged in.
	 *
	 * The choice is sticky and asked for with `deviceId: {exact}`, so a resume
	 * or a reconnect captures the same microphone rather than silently
	 * wandering back to the default. A device that can no longer be acquired
	 * falls back to the default with a non-fatal `onError`: a headset in a
	 * drawer must not cost the session.
	 */
	inputDeviceId?: string;
	/**
	 * Playback device for the remote audio, applied with `setSinkId()` on the
	 * SDK's own `<audio>` element. Chromium-only (`outputDeviceSupported()`);
	 * ignored with a non-fatal `onError` where it is not implemented, because a
	 * session is not worth failing over a sink.
	 */
	outputDeviceId?: string;
	/**
	 * Target depth for the receiver's own jitter buffer, in milliseconds
	 * (`RTCRtpReceiver.jitterBufferTarget`), applied to the audio receiver at
	 * leg-up and again after an ICE restart. Unset by default, which leaves
	 * the browser's adaptive target alone.
	 *
	 * **Chromium-only and experimental.** Firefox and Safari do not implement
	 * the attribute and the assignment is silently ignored there. What it buys
	 * is the client-side half of every latency this SDK reports: measured on
	 * this server, the browser settles on ~450 ms of held audio on a LAN leg
	 * and ~1150 ms over a WAN one and never revisits it, because egress sends
	 * a packet every 20 ms and NeteQ therefore never underruns into a
	 * re-sync. What it costs is concealment under real jitter — the buffer is
	 * there for a reason — so a value is a claim about the path, not a
	 * default: 100-150 ms is safe on a 50 ms-RTT, zero-loss link and nothing
	 * here defends a worse one.
	 */
	jitterBufferTarget?: number;
	/**
	 * The machine's audio devices changed — a headset plugged in, a USB
	 * microphone pulled out. Fires on `navigator.mediaDevices.devicechange`
	 * while the session is not closed, with the list already re-enumerated, so
	 * a picker re-renders without polling.
	 */
	onDevices?: (d: AudioDevices) => void;
	/**
	 * Debug tap: every frame, both directions. The outbound `session.start` is
	 * a shallow copy with its bearer token replaced by `[REDACTED]`; the frame
	 * written to the socket is unchanged.
	 */
	onFrame?: (dir: "in" | "out", f: Frame) => void;
	/**
	 * Client-side telemetry for the session trace (SPEC §12): playout edges,
	 * the client's own clock, mute edges, wake onsets, ICE state, `getStats`
	 * samples, input level, device and visibility changes. **Default true** —
	 * this is a debugging instrument for a session the consumer owns, and a
	 * trace with a hole where the browser should be is the one that cannot be
	 * argued with.
	 *
	 * False turns the collector off entirely: no timers, no `getStats` polls,
	 * no frames. `mark()` still works, because a mark is the user's own
	 * annotation rather than an observation about them.
	 */
	telemetry?: boolean;
}

/**
 * Never hand an application's debug/log callback a live bearer credential:
 * neither the token nor an agent URL (SPEC §18.14), which carries the agent's
 * own auth and reaches `session.start` when the config travels unsigned.
 */
function tappedFrame(f: Frame): Frame {
	if (f.t !== "session.start") return f;
	const agent = f.config?.agent;
	if (f.token === undefined && !agent) return f;
	return {
		...f,
		...(f.token !== undefined ? { token: "[REDACTED]" } : {}),
		...(agent && f.config ? { config: { ...f.config, agent: { ...agent, url: "[REDACTED]" } } } : {}),
	};
}

/**
 * SPEC §7 handshake budget. A server that accepts the socket but never sends
 * `session.ready`/`rtc.answer` must not hang `connect()` with the mic open.
 */
const HANDSHAKE_TIMEOUT_MS = 15_000;

/** SPEC §13 reconnect backoff: 250 ms doubling to a 5 s ceiling. */
const RECONNECT_BASE_MS = 250;
const RECONNECT_MAX_MS = 5_000;
/**
 * Consecutive failed attempts before the session is given up. Ten attempts
 * span ~33 s, comfortably past the node's 30 s re-attach grace: a client that
 * has not found a socket by then is not going to re-attach to anything, and a
 * fresh resume from `history` is a new user decision, not a retry.
 */
const RECONNECT_TRIES = 10;

/**
 * Client→server frames worth holding through a §13 socket gap. The session is
 * parked on the node with its jobs alive, so a `tool.result` a handler
 * finished during the gap is still the answer that job is blocked on —
 * dropping it hangs a job the server kept alive on purpose — and a typed turn
 * or an injected note is words the user believes they sent. A `session.mark`
 * carries its own `atMs`, so a late one still lands on the moment it was made
 * and losing it would lose exactly the instant somebody cared enough to flag.
 *
 * Everything else either describes a present that is re-established on
 * re-attach (`rtc.*`, `session.*`) or is stale the moment it is late.
 * `client.telemetry` is the clearest case of the latter: a batch replayed
 * minutes later is indistinguishable from fresh observation, so the collector
 * drops what it cannot send and counts the loss instead.
 */
const RESENDABLE: Partial<Record<ClientFrame["t"], true>> = {
	"tool.result": true,
	"text.send": true,
	"context.inject": true,
	"tool.cancel": true,
	"session.mark": true,
};

/**
 * Cap on held frames. A gap long enough to overflow this has already run past
 * the node's 30 s re-attach grace, so this is a bounded buffer and not a
 * store: past the cap the newest frame is dropped rather than evicting a
 * `tool.result` some job is still blocked on.
 */
const PENDING_MAX = 64;

/** SPEC §13 ICE restart: attempts per media leg, and the `disconnected` grace. */
const ICE_RESTART_TRIES = 3;
const ICE_DISCONNECT_GRACE_MS = 2_000;

/**
 * Wake on speech §6: how long after a commit the pre-roll backlog may begin
 * draining. It is the server's `metrics.FalseCutWindow`. Dropping silence any
 * earlier moves the user's next onset earlier in wall time and has it counted
 * as a false cut, which would corrupt the one number this project judges turn
 * detection by.
 */
const FALSE_CUT_WINDOW_MS = 1_500;

/**
 * Backoff after a wake whose session committed no turn: 30 s doubling to a 15
 * minute ceiling. `maxWakesPerMinute` bounds a noisy room; this bounds a
 * failing token mint, which would otherwise be retried against the customer's
 * endpoint on every onset all night.
 */
const WAKE_BACKOFF_BASE_MS = 30_000;
const WAKE_BACKOFF_MAX_MS = 15 * 60_000;

/**
 * The retained audio graph: one capture, one `AudioContext`, one worklet and
 * one destination node whose track is what every PeerConnection sends — live
 * or woken, so there is one code path and never a `replaceTrack`.
 *
 * It serves wake (`docs/wake-on-speech.md` §5), input level monitoring, or
 * both, and `needsGraph` is the predicate that decides whether a session has
 * one at all. A session with neither keeps the raw device track in the send
 * path and never builds any of this — latency is the product, and a worklet
 * hop nobody asked for is not free.
 *
 * `src` is mutable because a device switch replaces it: the sent track belongs
 * to `dest`, so swapping the source in front of the worklet changes the
 * microphone without a `replaceTrack` and without a renegotiation.
 *
 * `muted` is what the worklet was last *told*, not what the session wants —
 * `Session.isMuted` is that. Holding it here is what lets `applyMute` send a
 * message only when the gate actually changes: a session that never mutes
 * never posts to the render thread at all, on any of its media legs.
 */
interface AudioGraph {
	ctx: AudioContext;
	node: AudioWorkletNode;
	src: MediaStreamAudioSourceNode;
	dest: MediaStreamAudioDestinationNode;
	muted: boolean;
}

/**
 * main → worklet. `live` passes the capture straight through and holds no
 * ring; `armed` outputs silence while filling the bounded pre-roll ring and
 * watching for an onset; `payout` emits the ring ahead of live audio and stays
 * that far behind until drained; `drain` says whether the backlog may yet give
 * up its silent quanta; `level` sets the meter's cadence (0 is off); `mute`
 * gates the output to exact digital silence without touching what the detector
 * or the meter hears.
 */
type WakeCommand =
	| { type: "tune"; sustainMs: number; marginDb: number; preRollMs: number }
	| { type: "live" }
	| { type: "armed" }
	| { type: "payout" }
	| { type: "drain"; allow: boolean }
	| { type: "level"; intervalMs: number }
	| { type: "mute"; muted: boolean };

/**
 * worklet → main: one onset per armed detection, the backlog's progress, and
 * the meter's interval reports in every state.
 */
type WakeReport =
	| { type: "onset"; floorDb: number; levelDb: number }
	| { type: "backlog"; ms: number }
	| { type: "drained" }
	| { type: "level"; db: number; peakDb: number; floorDb: number };

function postWake(graph: AudioGraph, cmd: WakeCommand): void {
	graph.node.port.postMessage(cmd);
}

/** Tears the graph down. The context is what holds the render thread open. */
function releaseAudioGraph(graph: AudioGraph | null): void {
	if (!graph) return;
	graph.node.port.onmessage = null;
	graph.src.disconnect();
	graph.node.disconnect();
	void graph.ctx.close().catch(() => {
		/* a context that refuses to close is already gone */
	});
}

/**
 * `wake: true` takes every default, an object overrides field by field, and
 * absent or `false` is null. Null is the off switch every wake path checks:
 * with `wake` off nothing arms, nothing is retained across a suspend and no
 * pre-roll is held — whether or not a meter has put a graph in the path.
 */
function wakeTuning(opt: ConnectOptions["wake"]): WakeTuning | null {
	if (!opt) return null;
	return opt === true ? { ...WAKE_DEFAULTS } : { ...WAKE_DEFAULTS, ...opt };
}

/**
 * SPEC §11.1: browser AEC is non-negotiable. A function of the device id
 * because the choice is sticky — every later `getUserMedia` on the session
 * (resume, reconnect, wake, a device switch) asks for the same microphone —
 * and `{exact}` rather than a bare id so that a device which has gone away
 * fails loudly here instead of silently handing back the default.
 */
function MIC(deviceId: string | null): MediaStreamConstraints {
	return {
		audio: {
			echoCancellation: true,
			noiseSuppression: true,
			autoGainControl: true,
			channelCount: 1,
			sampleRate: 48000,
			...(deviceId ? { deviceId: { exact: deviceId } } : {}),
		},
	};
}

/** Default and bounds for `ConnectOptions.levelIntervalMs`. */
const LEVEL_INTERVAL_MS = 100;
const LEVEL_INTERVAL_MIN_MS = 20;
const LEVEL_INTERVAL_MAX_MS = 1_000;

/**
 * Playout burst thresholds (SPEC §12), for the energy poll that delimits one.
 * −50 dBFS is far under speech and far over the decoded silence this server
 * sends between turns: a live session measured its bursts at −0.4…−6 dBFS and
 * the silence between them at −77…−90, so the threshold sits ~44 dB over the
 * loudest silence and ~44 dB under the quietest speech. A voice soft enough
 * to cross it would be inaudible in the room.
 *
 * Two quiet polls (400 ms at `TELEMETRY_PLAYOUT_MS`) end a burst: longer than
 * the 300 ms this shipped with, because a fade at a sentence end or a pause
 * between two of them must not split one burst into a pair, and still inside
 * the resolution a barge-in is argued at.
 *
 * With a room-tone bed (`session.ready.bedDb`) the threshold rises to
 * `PLAYOUT_BED_MARGIN_DB` over the bed, never below −50: the bed random-walks
 * ±3 dB and the AGC pump only lowers it, so the bed always reads as quiet. The
 * server's −52 ceiling keeps the gate at −40, under the quiet windows of speech.
 */
const PLAYOUT_ON_DB = -50;
const PLAYOUT_BED_MARGIN_DB = 12;
const PLAYOUT_QUIET_POLLS = 2;
/**
 * How long the poll outlives the server's `speaking` state, in polls. The two
 * clocks are not the same thing: the server leaves `speaking` when it has
 * finished *producing* the turn, while the audio is still crossing the network
 * and the jitter buffer — and a one-word answer leaves `speaking` before the
 * poll has even taken the two samples an energy delta needs. A smoke run
 * against a real node lost exactly that burst with no tail at all. Two seconds
 * covers the buffer and the short answers; an audible sample re-arms it, so a
 * burst still playing keeps the poll alive on its own account.
 */
const PLAYOUT_TAIL_POLLS = 10;

/** Reads one numeric `getStats` field, which is typed as unknown per entry. */
function statNum(stat: Record<string, unknown>, key: string): number | undefined {
	const v = stat[key];
	return typeof v === "number" ? v : undefined;
}

/**
 * The duck fade (`playback.duck`, SPEC §6.3): how long the output gain takes
 * to reach its new value, and how many points the raised-cosine curve is
 * drawn with.
 *
 * 10 ms is the declick window: a step straight to zero is broadband — it is
 * the crackle heard at a barge-in — while a fade long enough to hear *as* a
 * fade starts around 20 ms. The shape is raised cosine rather than linear
 * because its first derivative is zero at both ends, so neither the start nor
 * the end of the fade is itself a discontinuity.
 *
 * 33 points over 10 ms is one control point every 0.3 ms (15 samples at
 * 48 kHz); `setValueCurveAtTime` interpolates linearly between them, and at
 * that spacing the residual error against the true cosine is under 0.1 %.
 */
const DUCK_FADE_MS = 10;
const DUCK_CURVE_POINTS = 33;

/**
 * The duck pulse (SPEC §11.1), which replaces a held duck when
 * `session.ready.bed` says egress carries a room-tone bed. The gain fades to
 * zero, holds for as long as the jitter buffer can still hold speech from
 * before the server's own fade, and fades back: everything arriving after
 * that is bed. The hold is the mean jitter-buffer delay over the last playout
 * poll interval plus 40 ms, clamped; with no stats report yet it is the
 * ceiling, because hearing the tail of a cut turn is worse than 250 ms
 * without the bed.
 */
const DUCK_PULSE_MARGIN_MS = 40;
const DUCK_PULSE_MIN_MS = 60;
const DUCK_PULSE_MAX_MS = 250;

function duckPulseHoldMs(jitterMs: number | null): number {
	if (jitterMs === null) return DUCK_PULSE_MAX_MS;
	return Math.min(DUCK_PULSE_MAX_MS, Math.max(DUCK_PULSE_MIN_MS, jitterMs + DUCK_PULSE_MARGIN_MS));
}

/**
 * A raised-cosine ramp from `from` to `to`, as `setValueCurveAtTime` wants it.
 * Allocated per ramp — a few times per turn, on the main thread, never in the
 * audio path.
 */
function fadeCurve(from: number, to: number): Float32Array {
	const curve = new Float32Array(DUCK_CURVE_POINTS);
	const last = DUCK_CURVE_POINTS - 1;
	for (let i = 0; i <= last; i++) {
		curve[i] = from + (to - from) * 0.5 * (1 - Math.cos((Math.PI * i) / last));
	}
	return curve;
}

/**
 * Ceiling for `ConnectOptions.jitterBufferTarget`. The attribute's own range
 * is [0, 4000] ms and it throws `RangeError` outside it, on a code path that
 * runs before the remote stream is attached.
 */
const JITTER_TARGET_MAX_MS = 4_000;

/** SPEC §11.1: 64 kbps cap, in-band FEC on, DTX off (server VAD needs real silence). */
const OPUS_FMTP: Record<string, string> = {
	maxaveragebitrate: "64000",
	useinbandfec: "1",
	usedtx: "0",
	stereo: "0",
};

const OPUS_RTPMAP = /^a=rtpmap:(\d+) opus\/48000/;
const FMTP = /^a=fmtp:(\d+) (.*)$/;

function fmtpBody(existing: string): string {
	const params = new Map<string, string>();
	for (const kv of existing.split(";")) {
		const eq = kv.indexOf("=");
		if (eq > 0) params.set(kv.slice(0, eq).trim(), kv.slice(eq + 1).trim());
		else if (kv.trim()) params.set(kv.trim(), "");
	}
	for (const [k, v] of Object.entries(OPUS_FMTP)) params.set(k, v);
	return [...params].map(([k, v]) => (v === "" ? k : `${k}=${v}`)).join(";");
}

/**
 * Pins the Opus parameters of an offer. Existing parameters (minptime,
 * sprop-stereo, …) survive; ours win on collision. Payload types advertising
 * Opus without an fmtp line get one.
 */
export function mungeOpus(sdp: string): string {
	const lines = sdp.split(/\r\n|\n/);
	const opus = new Set<string>();
	const hasFmtp = new Set<string>();
	for (const line of lines) {
		const rtpmap = OPUS_RTPMAP.exec(line);
		if (rtpmap?.[1]) opus.add(rtpmap[1]);
		const fmtp = FMTP.exec(line);
		if (fmtp?.[1]) hasFmtp.add(fmtp[1]);
	}
	if (opus.size === 0) return sdp;

	const out: string[] = [];
	for (const line of lines) {
		const fmtp = FMTP.exec(line);
		if (fmtp?.[1] && opus.has(fmtp[1])) {
			out.push(`a=fmtp:${fmtp[1]} ${fmtpBody(fmtp[2] ?? "")}`);
			continue;
		}
		out.push(line);
		const rtpmap = OPUS_RTPMAP.exec(line);
		if (rtpmap?.[1] && !hasFmtp.has(rtpmap[1])) {
			out.push(`a=fmtp:${rtpmap[1]} ${fmtpBody("")}`);
		}
	}
	return out.join("\r\n");
}

/**
 * The name the server will call a tool by. SPEC §8 declares schemas flat
 * (`{name, description, parameters}`); an already-wrapped OpenAI schema
 * (`{type:"function", function:{name}}`) names the same tool, so both are
 * accepted rather than silently registering a tool nothing can call.
 */
function toolName(schema: Record<string, unknown>): string {
	const flat = schema["name"];
	if (typeof flat === "string" && flat) return flat;
	const fn = schema["function"];
	if (fn && typeof fn === "object") {
		const nested = (fn as Record<string, unknown>)["name"];
		if (typeof nested === "string" && nested) return nested;
	}
	throw new Error(`tool schema has no name: ${JSON.stringify(schema)}`);
}

/**
 * Where the session object is in its own lifecycle, which is not what
 * `Status` reports: `Status` is what to show a user, this is what the SDK may
 * do next. The two differ exactly during a §13 gap, where the session is
 * `"reconnecting"` here while the server-side state it will re-attach to is
 * still whatever it was.
 *
 * `"suspending"` is the window inside `suspend()`: the request is on the wire
 * and `session.suspended` has not answered yet. It is its own phase because a
 * socket dropping in that window must not reconnect — re-attaching the session
 * the user just asked to release is the opposite of what they pressed.
 */
type Phase = "new" | "live" | "reconnecting" | "suspending" | "suspended" | "closed";

export class Session {
	/** Live transcript; safe to hand straight back as `history` on resume. */
	readonly history: Message[] = [];
	/**
	 * Jobs the server reports as running (SPEC §9), from the latest `state`
	 * frame, each backend job with the latest `progress` it had spoken. Empty
	 * until one carries a `running` array, and empty again as soon as one
	 * omits it.
	 */
	jobs: RunningJob[] = [];
	/**
	 * The agent connection (SPEC §18.14) from the latest `state` frame:
	 * whether the node holds it open and the agent's last status. Undefined
	 * when the config names no agent, and while no session is live.
	 */
	agent: AgentState | undefined = undefined;
	state: Status = "connecting";
	sessionId = "";
	/**
	 * The conversation, from `session.ready` (SPEC §18.1): the token's `conv`,
	 * or a node-minted id when it carries none. Unlike `sessionId` it survives
	 * suspend, resume and re-attach, so it is the id a page hands its backend.
	 * Empty until the first `session.ready`.
	 */
	conversationId = "";
	/**
	 * Pre-roll still queued ahead of live audio on a woken turn (§6), in
	 * milliseconds, as the worklet last reported it: 0 once it has drained,
	 * and always 0 with `wake` off. A woken turn runs this far behind real
	 * time, so it is the number to read when its latency looks wrong.
	 */
	wakeBacklogMs = 0;
	/**
	 * The last onset the detector accepted: when, and how far over its tracked
	 * noise floor. M2 of `docs/wake-on-speech.md` §11 is the overnight
	 * false-wake count and the levels that produced it, so a page counting
	 * wakes in `onWake` reads this.
	 */
	lastWake: { at: number; floorDb: number; levelDb: number } | null = null;
	/**
	 * The latest input level report, or null before the first one and whenever
	 * the graph is gone (no `onLevel`, a suspend that released the capture, a
	 * graph that failed to build). A meter SHOULD read this from
	 * `requestAnimationFrame` rather than re-render inside `onLevel`: the
	 * reports arrive on the audio thread's cadence, which is nobody's frame
	 * rate, and a bar drawn from the newest value is the same bar.
	 *
	 * `muted` here is never behind `s.muted`: `setMuted()` re-stamps this
	 * snapshot on a fresh object when the gate flips, so the rAF loop above
	 * cannot paint the old state for a report interval. The levels are still
	 * the last real measurement, and no `onLevel` fires for the re-stamp.
	 */
	inputLevel: InputLevel | null = null;
	/**
	 * The capture device this session is using, or null for the system default.
	 * Set by `setInputDevice()`, and cleared back to null when a sticky choice
	 * can no longer be acquired — so it is the device the session *has*, never
	 * the one it wanted.
	 */
	inputDeviceId: string | null = null;
	/** The playback device the remote audio is on, or null for the system default. */
	outputDeviceId: string | null = null;

	private readonly opts: ConnectOptions;
	private ws: WebSocket | null = null;
	private pc: RTCPeerConnection | null = null;
	private mic: MediaStream | null = null;
	private audio: HTMLAudioElement | null = null;
	/**
	 * The remote audio's own render graph: source → gain → destination, on a
	 * context of its own. It exists so the output has a gain a fade can be
	 * scheduled on, which is the whole of the barge-in cut — the audio a cut
	 * has to silence is already inside the receiver's jitter buffer, so
	 * nothing the server or the control socket does can reach it, and the only
	 * place left is the last hop before the speaker.
	 *
	 * Null on a session whose remote track has not landed, and on any browser
	 * where the context could not be built or would not start; the `<audio>`
	 * element plays the stream directly there (`elementOut`).
	 */
	private outCtx: AudioContext | null = null;
	private outGain: GainNode | null = null;
	private outSrc: MediaStreamAudioSourceNode | null = null;
	/**
	 * The element is the one making sound, because the render graph is not
	 * (yet) running. The two are mutually exclusive: whichever is playing, the
	 * other is muted, and `syncOutputRoute` is the single place that decides.
	 */
	private elementOut = true;
	/** An output context that would not start has been reported; report it once. */
	private outDeaf = false;
	/** The output is faded down. */
	private ducked = false;
	/**
	 * What put it down, which decides what may bring it back up. A duck the
	 * server asked for with a frame is lifted by the matching `on:false`; a
	 * duck a confirmed interrupt caused gets no `on:false` at all — the server
	 * resumes with the *next* turn — so it is also lifted by the next
	 * `speaking`, which is the belt for a lost frame.
	 */
	private duckHold: "frame" | "clear" | null = null;
	/**
	 * `session.ready.bed`: the output carries a room-tone bed, so a duck is a
	 * pulse on the gain and `ducked` stays a purely logical flag.
	 */
	private bed = false;
	/** The playout energy gate in dBFS, raised over `session.ready.bedDb`. */
	private playoutOnDb = PLAYOUT_ON_DB;
	/** Mean jitter-buffer delay in ms since the previous playout poll; sizes the pulse. */
	private jitterMs: number | null = null;
	/** The output context stopped under us; ask for it back, then re-route. */
	private readonly onOutState = (): void => {
		this.wakeOutput();
		this.syncOutputRoute();
	};
	/** Remote candidates that arrived before the answer to the current offer. */
	private pendingIce: RTCIceCandidateInit[] = [];
	private assistant = new Map<string, SpeechMessage>();
	/** Final user turns by `messageId`, so `transcript.user.amend` can find one. */
	private userTurns = new Map<string, SpeechMessage>();
	/** Client tools by name, from `ConnectOptions.tools`. */
	private readonly tools = new Map<string, ToolDefinition>();
	/**
	 * `tool.call` ids already served. A reconnect replays frames (SPEC §13) and
	 * a job must run exactly once, so a repeat is dropped rather than re-run.
	 */
	private readonly served = new Set<string>();
	/** Handlers still running, by job id; a `tool.cancel` aborts one. */
	private readonly runningTools = new Map<string, AbortController>();
	/** Job ids with a `tool_call` history record, and with a settled one. */
	private readonly toolCalls = new Set<string>();
	private readonly toolResults = new Set<string>();
	/** `transcript.context` message ids already recorded, so a replay adds no second row. */
	private readonly contextRows = new Set<string>();
	private waitReady: PromiseWithResolvers<SessionReady> | null = null;
	private waitAnswer: PromiseWithResolvers<string> | null = null;
	private waitSuspended: PromiseWithResolvers<void> | null = null;
	/**
	 * Rejected to abandon an in-flight handshake (error frame or teardown);
	 * null between handshakes, so an error outside one aborts nothing.
	 */
	private abort: PromiseWithResolvers<never> | null = null;
	private phase: Phase = "new";
	/** The phase `suspend()` interrupted, restored if it is never answered. */
	private suspendFrom: Phase = "live";
	/**
	 * The highest `seq` processed. It rides on every `session.start` that names
	 * a `sessionId` so a re-attaching node knows where to resume its replay ring
	 * (SPEC §13), and resets whenever the server answers with a fresh session,
	 * because sequence numbers are per session and start again at 1.
	 */
	private ack = 0;
	/** Consecutive reconnect attempts since the last successful handshake. */
	private attempts = 0;
	private reconnectTimer: Timer | null = null;
	/** The in-flight `resume()`, so a second call joins it instead of racing. */
	private resuming: Promise<void> | null = null;
	/** The `session.start` round in flight, if any; a second caller joins it. */
	private inFlight: Promise<SessionReady> | null = null;
	/**
	 * Frames a §13 socket gap could not carry, oldest first, flushed on the
	 * re-attached socket. Dropped rather than flushed when the server answers
	 * with a fresh session: the job ids they name died with the old one.
	 */
	private pending: ClientFrame[] = [];
	/**
	 * Why the last reconnect attempt failed. The give-up frame reports it, so a
	 * `/token` endpoint answering 401 — the one failure the `token` callback
	 * exists to expose — is not misreported as a lost socket.
	 */
	private lastError: Error | null = null;
	private iceAttempts = 0;
	private iceTimer: Timer | null = null;
	private restarting = false;
	/** Wake tuning, or null when `wake` is off. Null is the off switch. */
	private readonly wake: WakeTuning | null;
	/**
	 * Why the session was last released. Kept because retention and arming
	 * differ by reason: `"idle"` keeps the capture and arms the detector,
	 * `"error"` keeps the capture for the auto-resume without arming it, and
	 * `"client"` releases everything, because the explicit pause has to be a
	 * real pause. `armWake` is where it is read.
	 */
	private suspendReason: SuspendReason | null = null;
	/**
	 * The audio graph, built with the first media leg that needs one and kept
	 * across an idle suspend when `wake` is on. It serves the wake detector,
	 * the input meter, or both — `needsGraph` says which sessions have one, and
	 * every wake-specific behaviour is gated on `this.wake` rather than on
	 * this, because a monitoring-only session has a graph and must never arm,
	 * never hold a pre-roll and never retain a capture across a suspend.
	 */
	private graph: AudioGraph | null = null;
	/** The worklet is filling the pre-roll ring and watching for an onset. */
	private armed = false;
	/** This resume came from an onset: it pays the ring out and greets nothing. */
	private woken = false;
	/** One-shot: rides on the next `session.start` only, never a reconnect or re-attach. */
	private wakeStart = false;
	/** The woken session committed a turn, so the wake was not a false one. */
	private wakeCommitted = false;
	/** Wall clock of each wake inside the last minute, for `maxWakesPerMinute`. */
	private wakeTimes: number[] = [];
	/** Consecutive wakes that committed nothing, and the backoff they bought. */
	private wakeMisses = 0;
	private wakeBlockedUntil = 0;
	/** A woken turn's backlog is ours to drain; cleared when the worklet says `drained`. */
	private draining = false;
	private drainTimer: Timer | null = null;
	/**
	 * Drops the context watch of `watchAudioContext`, non-null for as long as
	 * the graph it watches exists. A listener on the page's `document` has to
	 * be removable or it outlives every session the page opens.
	 */
	private ctxWatch: (() => void) | null = null;
	/**
	 * A context's refusal to resume has been reported. Latched for the life of
	 * the watch so a browser that wants a gesture is announced once, not on
	 * every visibility flip.
	 */
	private ctxDeaf = false;
	/**
	 * Muted locally: the server is handed nothing to hear. Two mechanisms, one
	 * behaviour — the worklet gates its output when there is a graph, and the
	 * capture tracks are disabled when there is not — and `applyMute` is the
	 * single place that knows which. Never a frame on the wire.
	 */
	private isMuted: boolean;
	/** `onLevel`'s cadence, clamped once in the constructor rather than at each use. */
	private readonly levelIntervalMs: number;
	/**
	 * Orders capture swaps. A device change and a user's `setInputDevice()` can
	 * be in flight at the same time; run concurrently each would stop the
	 * stream it saw at entry and orphan a live capture. Every swap queues
	 * behind this instead.
	 */
	private swapping: Promise<void> = Promise.resolve();
	/**
	 * The `devicechange` listener, held so `destroy()` can take it off again: a
	 * listener that outlives its session is a leak that keeps every closed
	 * session's callbacks reachable from `navigator.mediaDevices`.
	 */
	private readonly onDeviceChange = (): void => {
		void this.devicesChanged();
	};
	/**
	 * The client half of the session trace (SPEC §12), or null when
	 * `telemetry: false` turned the collector off. Every report goes through
	 * the optional call, so off costs one null check per edge and no timers.
	 */
	private readonly telemetry: Telemetry | null;
	/** The 1 s ticker: `level` every tick, `clock` and `stats` every fifth. */
	private teleTimer: Timer | null = null;
	private teleTicks = 0;
	/** The playout poll, alive only while there is playout to measure. */
	private playoutTimer: Timer | null = null;
	/** Whether audio is audibly playing out, as the last edge left it. */
	private playingOut = false;
	/** Consecutive quiet polls; `PLAYOUT_QUIET_POLLS` of them end a burst. */
	private quietPolls = 0;
	/** Polls the playout poll may still run with the state no longer `speaking`. */
	private playoutTail = 0;
	/** The previous energy sample: playout level is a delta, not a reading. */
	private lastEnergy: { energy: number; dur: number } | null = null;
	/**
	 * The previous jitter-buffer counters. The pulse hold wants how deep the
	 * buffer is now, and the cumulative mean is the whole call's, which a
	 * buffer that deepened mid-call drags behind by minutes.
	 */
	private lastJitter: { delay: number; count: number } | null = null;
	/** A `getStats()` in flight, per cadence: a poll must not queue on itself. */
	private playoutBusy = false;
	private statsBusy = false;
	/** Invalidates asynchronous stats callbacks when their media/session lifetime ends. */
	private statsGeneration = 0;
	/** Client-reported 24 kHz heard clock; advanced only across audible bursts. */
	private outputPlayedSamples = 0;
	private outputBurstAt: number | null = null;
	private outputAudibleAt: number | null = null;
	private outputReportedAt = 0;
	/** Last position put on the wire; equal positions are never resent. */
	private outputLastReportedSamples: number | null = null;
	/**
	 * This second's level reports, folded down in place. Scalars rather than an
	 * object: a report arrives every 20-100 ms for the whole session — it is
	 * the one telemetry path on a hot loop — and the fold must not allocate.
	 * `levelN` 0 means nothing was measured this second.
	 */
	private levelN = 0;
	private levelMinDb = 0;
	private levelMaxDb = 0;
	private levelPeakDb = 0;
	private levelFloorDb = 0;
	/** Labels from the last enumeration, so a `device` event can name one. */
	private readonly deviceLabels = new Map<string, string>();
	/**
	 * Tab visibility, held so `destroy()` can take it off again. Two readers:
	 * a backgrounded tab is throttled hard, so this is what tells a slow turn
	 * in the trace from a tab nobody was looking at — and coming back is the
	 * second chance to restart an output context the platform suspended,
	 * exactly as the capture graph's own watch uses it, for a browser that
	 * will not resume a hidden tab's context on its own.
	 */
	private readonly onVisibility = (): void => {
		this.telemetry?.record("visibility", { state: document.visibilityState });
		if (document.visibilityState === "visible") this.wakeOutput();
	};

	constructor(opts: ConnectOptions) {
		this.opts = opts;
		this.wake = wakeTuning(opts.wake);
		this.isMuted = opts.muted === true;
		this.levelIntervalMs = Math.min(
			LEVEL_INTERVAL_MAX_MS,
			Math.max(LEVEL_INTERVAL_MIN_MS, opts.levelIntervalMs ?? LEVEL_INTERVAL_MS),
		);
		this.inputDeviceId = opts.inputDeviceId ?? null;
		this.outputDeviceId = opts.outputDeviceId ?? null;
		// One listener for the life of the session. It is what re-renders a
		// picker and what notices that the microphone this session is holding
		// has just been unplugged; both are cheap and neither touches the audio
		// path. Guarded rather than assumed: a `MediaDevices` without the
		// EventTarget half — an insecure context, a test double — must not cost
		// a constructor that has not opened anything yet.
		navigator.mediaDevices?.addEventListener?.("devicechange", this.onDeviceChange);
		// The flight recorder's client half (SPEC §12). It writes straight to
		// the socket rather than through `send()`: a batch is worthless once it
		// is late, so it must never be held for a re-attach.
		//
		// `phase`, not just the socket: an open socket is not a bound session.
		// Between a release and the next `session.start` — a suspend, the
		// window inside a re-attach handshake — the node answers anything else
		// with `protocol_error`, and since this SDK records the errors it
		// reports, writing there would record the refusal, flush it a second
		// later, and be refused again for as long as the pause lasted.
		this.telemetry =
			opts.telemetry === false
				? null
				: new Telemetry((f) => {
						const ws = this.ws;
						if (this.phase !== "live" || !ws || ws.readyState !== WebSocket.OPEN) return false;
						ws.send(JSON.stringify(f));
						this.opts.onFrame?.("out", tappedFrame(f));
						return true;
					});
		// `globalThis.document`, not `document`: a consumer (and every test
		// that does not need a page) may run this constructor where the binding
		// does not exist at all, and a bare reference would throw rather than
		// skip the listener. Registered whether or not the collector exists,
		// because its other job — restarting an output context the platform
		// suspended — is audible rather than observational.
		globalThis.document?.addEventListener("visibilitychange", this.onVisibility);
		for (const t of opts.tools ?? []) this.tools.set(toolName(t.schema), t);
		for (const m of opts.history ?? []) {
			this.history.push(m);
			if (m.role === "tool_call") this.toolCalls.add(m.jobId);
			if (m.role === "tool_result") this.toolResults.add(m.jobId);
		}
	}

	async start(): Promise<void> {
		if (this.opts.token === undefined && this.opts.config === undefined) {
			throw new Error(
				"Liaison.connect needs a token (SPEC §8), or a config when the node runs with --no-auth",
			);
		}
		try {
			await this.handshake();
		} catch (err) {
			this.destroy();
			throw err;
		}
	}

	/** Typed user turn; the server treats it as a committed transcript (SPEC §7.1). */
	sendText(text: string): void {
		this.send({ t: "text.send", text });
	}

	/**
	 * Puts a note into the conversation from outside the speech (SPEC §7.1):
	 * the page's own state, a form the user just submitted, a slow job's
	 * outcome. `respond` asks the liaison to speak about it now; the default is
	 * silent, which is the common case — the note is context for the next turn,
	 * not an interruption of this one.
	 *
	 * The note is recorded in `s.history` as a `{role:"context"}` row, because
	 * `history` is the durable conversation and this client owns it (SPEC §5.3):
	 * the server persists the same row but answers no transcript frame for it,
	 * so a note the SDK did not record would simply vanish at the next resume —
	 * and injecting on every agent event is exactly what a bridge does. The row
	 * goes in when the frame reaches the socket, not when this returns, so a
	 * note the SDK could not deliver leaves no phantom behind (see `send`).
	 *
	 * `onTranscript` deliberately does **not** fire: it reports what the server
	 * told us about the conversation, and `sendText()` is silent on it for the
	 * same reason. The caller already has this text.
	 */
	inject(text: string, opts?: { respond?: boolean }): void {
		this.send({ t: "context.inject", text, respond: opts?.respond ?? false });
	}

	/**
	 * Cancels a running job (SPEC §9): the user hit stop. A `tool.call`'s `id`
	 * *is* its job id, so the same frame reaches a client, http or built-in
	 * job; the local handler, if this client is the one running it, is aborted
	 * without waiting for the server to say so. An unknown id is ignored
	 * server-side and answers nothing.
	 */
	cancelJob(jobId: string): void {
		this.send({ t: "tool.cancel", id: jobId });
		this.abortJob(jobId);
	}

	/**
	 * Tags this moment in the session's trace (SPEC §12) — the way to say
	 * *that* was bad while it is still true. `label` is the verdict ("bad",
	 * "good", whatever a page's own buttons mean) and `note` is free text.
	 * `atMs` is stamped at the gesture rather than at the send, so a mark held
	 * through a §13 socket gap still lands on the moment it was made.
	 *
	 * Independent of `ConnectOptions.telemetry`: a session that turned the
	 * collector off can still mark, because a mark is the user annotating the
	 * session and not the SDK observing them.
	 */
	mark(label: string, note?: string): void {
		this.send({ t: "session.mark", label, ...(note ? { note } : {}), atMs: Date.now() });
	}

	// ------------------------------------------------------------ media controls

	/** Muted locally, so the server is hearing nothing from this client. */
	get muted(): boolean {
		return this.isMuted;
	}

	mute(): void {
		this.setMuted(true);
	}

	unmute(): void {
		this.setMuted(false);
	}

	/**
	 * Mutes or unmutes the capture. Synchronous, local, and silent on the wire:
	 * there is no mute frame in the protocol and the server does not need one —
	 * what it gets is digital silence, which its VAD reads as the user not
	 * talking, exactly as it reads a quiet room.
	 *
	 * Two consequences worth knowing. A mute in the middle of a turn commits
	 * that turn: frames stop carrying speech, so the server's turn rules see a
	 * hold and then an end, and the audio-clock watchdog
	 * (`docs/wake-on-speech.md` §7) closes the case where they see nothing at
	 * all. And with `wake` on, muting does **not** disarm the detector: onsets
	 * while muted are ignored — no resume, no wake counted, no backoff charged
	 * — so unmuting restores wake on speech with no further call.
	 */
	setMuted(muted: boolean): void {
		const moved = this.isMuted !== muted;
		this.isMuted = muted;
		this.applyMute();
		if (moved) this.telemetry?.record(muted ? "mute" : "unmute");
		// The gate moved, so the stored report is stale in the one field the
		// main thread owns. `s.inputLevel` is what §7 tells a meter to paint
		// from in a `requestAnimationFrame` loop, and rAF runs long before the
		// next worklet report does: leaving the old stamp there draws a muted
		// indicator for up to a whole report interval after the button opened
		// the gate. Re-stamp it here, where the gate changes, on a *fresh*
		// object — a consumer may be holding the report it was handed, and
		// rewriting that behind its back is worse than the lag. The levels
		// stand: they are the last real measurement and still true. And no
		// `onLevel` fires, because nothing was measured and a synthetic report
		// would corrupt a consumer counting reports or timing the cadence.
		const level = this.inputLevel;
		if (level !== null && level.muted !== muted) this.inputLevel = { ...level, muted };
	}

	/**
	 * Switches the capture device. `null` is the system default. The same id
	 * the session already has is a no-op, prompt and all.
	 *
	 * The new capture is acquired **first** and the running one is left alone
	 * until it succeeds, so a device that cannot be opened costs the caller a
	 * rejected promise and nothing else — the session keeps working on the
	 * microphone it had. On success the old capture is stopped, mute is
	 * re-applied to the new one, and `s.inputLevel` keeps flowing.
	 *
	 * With a graph in the path this is a source swap in front of the worklet:
	 * the track the PeerConnection sends belongs to the destination node, so
	 * there is no `replaceTrack` and no renegotiation, and neither the
	 * detector's noise floor nor the meter's interval is disturbed. Without
	 * one, the device track *is* the sent track and the sender is told.
	 *
	 * The choice is sticky: every later `getUserMedia` this session makes — a
	 * resume, a reconnect attempt, a wake — asks for the same device.
	 */
	async setInputDevice(deviceId: string | null): Promise<void> {
		if (this.phase === "closed") throw new Error("session closed");
		const want = deviceId ?? null;
		if (want === this.inputDeviceId) return;
		// Nothing is capturing: before the first leg, or suspended with the
		// capture released. Recording the choice is the whole of the work —
		// acquiring one here would light the recording indicator on a session
		// the user is being shown as paused, and the next leg's `getUserMedia`
		// takes the sticky id anyway.
		if (!this.mic) {
			this.inputDeviceId = want;
			this.noteDevice("input", want);
			return;
		}
		const next = await navigator.mediaDevices.getUserMedia(MIC(want));
		// A suspend, or an `end()`, landed while the permission prompt was up:
		// either one released the capture, which is why `this.mic` is the test
		// and the phase is not. The capture just acquired has nothing to swap
		// into and would hold the recording indicator lit on a session the user
		// is shown as paused; the choice still stands for the next leg to take.
		if (!this.mic) {
			for (const t of next.getTracks()) t.stop();
			this.inputDeviceId = want;
			this.noteDevice("input", want);
			return;
		}
		try {
			await this.swapCapture(next);
		} catch (err) {
			// The swap failed with the old capture still running and still the
			// session's: stop the one that never made it in rather than leaving
			// two indicators lit, and let the caller see why.
			for (const t of next.getTracks()) t.stop();
			throw err;
		}
		this.inputDeviceId = want;
		this.noteDevice("input", want);
	}

	/**
	 * Switches the playback device (`null` is the system default), by
	 * `setSinkId()` on the SDK's own hidden `<audio>` element. The element is
	 * created here when the first remote track has not landed yet, so a picker
	 * works before the liaison has said anything.
	 *
	 * Sticky: re-applied whenever that element is created. Rejects where the
	 * browser does not implement output selection at all
	 * (`outputDeviceSupported()` — Safari and Firefox today), and a
	 * `NotAllowedError` from the browser's own permission rule is passed
	 * straight through: both are the caller's to show.
	 */
	async setOutputDevice(deviceId: string | null): Promise<void> {
		if (this.phase === "closed") throw new Error("session closed");
		if (!outputDeviceSupported()) {
			throw new Error(
				"this browser cannot choose an audio output device: HTMLMediaElement.setSinkId is not implemented",
			);
		}
		const want = deviceId ?? null;
		const previous = this.outputDeviceId;
		// Recorded *before* the element is created, because creating one is
		// where the sticky choice gets applied: leaving the field on the
		// previous sink here would have the creation path name that one while
		// this call names the new one, two `setSinkId` promises racing on one
		// element with only one of them awaited.
		this.outputDeviceId = want;
		const el = this.audioElement();
		try {
			// `""` is the spec's own spelling of "the default sink", and the
			// only way back to it once a sink has been named. Both objects get
			// it: the element because it is the fallback route and the one
			// `outputDeviceSupported()` describes, the output context because
			// on the graph route it is the one actually making sound.
			await el.setSinkId(want ?? "");
			await this.nameOutputSink(want);
			this.noteDevice("output", want);
		} catch (err) {
			// The field describes where the audio *is*: a refused sink — the
			// browser's own permission rule, a device that went away between
			// the pick and the call — rolls back and tells the caller. Only if
			// the field is still this call's own choice, though: a slow
			// rejection must not overwrite a newer pick that has already taken
			// effect, or the audio would play on B while this said the sink was
			// whatever preceded A.
			if (this.outputDeviceId === want) {
				this.outputDeviceId = previous;
				// This call may be the one that created the element, and
				// `attachAudio` only applies the sticky sink to an element it
				// creates itself — so a rejection here would otherwise strand a
				// connect-time `outputDeviceId` and play the whole session out
				// of the default device while the field claimed otherwise. It
				// cannot race: this call has settled.
				this.applyStickySink(el);
				// Same for the graph route, which may already be on the sink
				// the element refused (or the other way round): whichever half
				// took, the field is back on `previous` and both have to be.
				void this.nameOutputSink(previous).catch(() => {
					/* the rejection the caller is about to see is the report */
				});
			}
			throw err;
		}
	}

	/**
	 * Releases the session (SPEC §10): the server drops the vendor sockets, the
	 * PeerConnection and the job tree, and answers `session.suspended`. This
	 * object stays usable — `s.history` is the conversation and `resume()`
	 * brings it back on any node — and the control socket is deliberately left
	 * open, released, because the server will take the next `session.start` on
	 * it. Resolves when the server confirms. Calling it on a session that is
	 * already suspended asks the server for nothing, but still turns that
	 * suspend into the user's: with `wake` on there is a capture and an armed
	 * detector to put down, and a pause that left them running is not one.
	 */
	async suspend(): Promise<void> {
		if (this.phase === "closed") throw new Error("session closed");
		if (this.phase === "suspended") {
			// Already released server-side, so there is nothing to ask for — but
			// an `"idle"` or `"error"` suspend with `wake` on is still holding
			// the capture, and may still be armed (§7). That is not the pause
			// this call promises. Downgrading it to a client pause is the whole
			// of the work: the detector disarms, the recording indicator goes
			// out, and only an explicit `resume()` brings the session back.
			// Idempotent — a second call on a pause that is already the user's
			// has nothing left to do, and neither has one on a `"replaced"`
			// suspend, which already released everything and already waits for
			// the user (§18.1).
			if (this.suspendReason !== "client" && this.suspendReason !== "replaced") this.enterSuspended("client");
			return;
		}
		this.clearReconnect();
		// No socket to ask: whatever held this session is already gone, so the
		// local half of a suspend is the whole of it. The re-attach identity goes
		// with it — keeping `sessionId`/`ack` would have the next resume()
		// renegotiate a brand-new PeerConnection into the node's still-parked
		// session, which is the one thing a re-attach must never do. Without
		// them the resume is honestly a fresh session from `history` and the
		// park window expires on its own.
		if (this.ws?.readyState !== WebSocket.OPEN) {
			this.sessionId = "";
			this.ack = 0;
			this.enterSuspended("client");
			return;
		}
		// A second caller joins the request already on the wire instead of
		// sending a second frame. `suspendFrom` is the phase the first one
		// interrupted: restoring it is what leaves a timed-out suspend
		// reconnectable rather than stuck in a window nothing will answer.
		const joined = this.phase === "suspending";
		const wait = (this.waitSuspended ??= Promise.withResolvers<void>());
		if (!joined) {
			this.suspendFrom = this.phase;
			// The trace's last batch goes out here, for the same reason `end()`
			// writes one ahead of `session.end`: this is the last moment the
			// session is bound. The node closes the trace on the way out of
			// `session.suspend`, and everything after that — including the
			// `session.suspended` that tells us it happened — has no file to
			// land in. An `"idle"` or `"error"` suspend has no such moment and
			// loses its last partial second with the session.
			this.telemetry?.flush();
			this.phase = "suspending";
			this.send({ t: "session.suspend" });
		}
		try {
			await this.race(
				wait.promise,
				`session.suspend was not answered within ${HANDSHAKE_TIMEOUT_MS} ms`,
			);
		} catch (err) {
			// Unanswered, and nothing was released: put the session back where it
			// was so the socket is still reconnectable and the caller can retry.
			if (this.phase === "suspending") this.phase = this.suspendFrom;
			throw err;
		} finally {
			if (this.waitSuspended === wait) this.waitSuspended = null;
		}
	}

	/**
	 * Brings a suspended session back (SPEC §10) — the tap-to-resume gesture.
	 * A fresh token, `session.start` carrying the whole of `s.history`, and a
	 * new media leg; the released socket is reused when it is still open,
	 * because the server is waiting for exactly this frame on it.
	 *
	 * The session id is the server's to mint: a resume is a *new* session built
	 * from the history, so `s.sessionId` changes: the id sent up is only the
	 * correlator a re-attach (SPEC §13) would have matched, and an unknown one
	 * is ignored. Nothing may assume id continuity across a suspend.
	 *
	 * Safe to call twice: the second call awaits the first. A live session is a
	 * no-op. On failure the session stays suspended with its history intact and
	 * without the socket the failure happened on, so the user can simply tap
	 * again.
	 */
	async resume(): Promise<void> {
		if (this.phase === "closed") throw new Error("session closed");
		if (this.phase === "live") return;
		const pending = this.resuming;
		if (pending) return pending;

		// A resume is a user decision and outranks the backoff clock: whatever
		// attempt was scheduled is dropped in favour of this one.
		this.clearReconnect();
		this.attempts = 0;
		this.phase = "reconnecting";
		this.setState("connecting");
		const run = this.handshake().then(
			() => {
				/* handshake() put the session back in `live` */
			},
			(err: unknown) => {
				if (this.phase === "reconnecting") {
					this.phase = "suspended";
					this.setState("suspended");
				}
				throw err;
			},
		);
		this.resuming = run;
		try {
			await run;
		} finally {
			if (this.resuming === run) this.resuming = null;
		}
	}

	/** Ends the session and releases mic, PeerConnection, socket and audio element. */
	end(): void {
		// The trace's last batch goes out **ahead of** `session.end`, not behind
		// it: the server ends the session on that frame and closes the socket
		// with it, so a batch written afterwards is read by nobody — measured,
		// on a live node, as a session whose trace had no closing line. The
		// teardown in `destroy()` stamps the same closing state for every other
		// path out; `Telemetry.stop()` makes the second one a no-op.
		this.telemetry?.record("state", { state: "closed" });
		this.stopTelemetry(true);
		if (this.ws?.readyState === WebSocket.OPEN) this.send({ t: "session.end" });
		this.destroy();
	}

	// ------------------------------------------------------------------ internals

	/**
	 * Runs one `session.start` round, or joins the one already in flight: a
	 * `resume()` landing on top of a reconnect attempt — the user tapping while
	 * the backoff was already working — must not put a second start down the
	 * same socket and race it for `session.ready`.
	 */
	private handshake(): Promise<SessionReady> {
		const running = this.inFlight;
		if (running) return running;
		const run = this.runHandshake();
		this.inFlight = run;
		void run.catch(() => {
			/* every caller reports its own failure; this only keeps the join safe */
		});
		return run.finally(() => {
			if (this.inFlight === run) this.inFlight = null;
		});
	}

	/**
	 * One `session.start` round: token, start frame, `session.ready`, and —
	 * unless the node re-attached the live session whose media we still hold —
	 * a media leg built from scratch. The first connect, every reconnect
	 * attempt and `resume()` all run this; they differ only in what
	 * `startFrame()` puts on the wire and in what they do with a failure.
	 */
	private async runHandshake(): Promise<SessionReady> {
		const abort = Promise.withResolvers<never>();
		abort.promise.catch(() => {
			/* raced in deadline(); the guard covers a reject between two waits */
		});
		this.abort = abort;
		try {
			// The token first, before the socket and long before the mic: a
			// backend that will not mint one is a failure the user should see
			// without the recording indicator ever lighting up. A session with
			// no token source at all does not wait a turn for that answer.
			const token =
				this.opts.token === undefined ? undefined : await this.resolveToken();
			// `end()` before the socket opens detaches `onopen`, so this wait needs
			// the abort signal too or `start()` would never settle.
			if (this.ws?.readyState !== WebSocket.OPEN) {
				await this.deadline(this.openSocket(), "the control socket to open");
			}
			this.send(this.startFrame(token));
			this.waitReady = Promise.withResolvers<SessionReady>();
			const ready = await this.deadline(this.waitReady.promise, "session.ready");
			// Always the server's id, never the one we asked to re-attach to: a
			// resume is answered with a brand-new session under a brand-new id,
			// and only a re-attach echoes the id we sent.
			this.sessionId = ready.sessionId;
			this.conversationId = ready.conversationId ?? "";

			// SPEC §13 re-attach: the node still holds this session's media, so
			// the PeerConnection, the mic and the audio element are left exactly
			// as they are — no offer, no second getUserMedia — and the server
			// re-sends `state` and replays the frames past our `ack`.
			if (!ready.reattached) this.ack = 0; // a fresh session numbers from 1
			// The frames the gap could not carry: the same session with the same
			// jobs still wants them, a fresh one built from `history` cannot
			// place them because the job ids they name are gone.
			if (ready.reattached) this.flushPending();
			else this.pending = [];
			if (!ready.reattached || !this.pc) {
				await this.negotiateMedia(ready.iceServers ?? [], abort);
			}
			// A `session.suspended` that landed between the awaits above took the
			// session with it. Reporting `live` here would leave `phase` at
			// "live" under `state` "suspended", which makes the next resume() a
			// silent no-op with the mic still on.
			if (!this.owns(abort)) throw new Error("handshake abandoned: the session was released");
			this.phase = "live";
			this.attempts = 0;
			this.lastError = null;
			this.startTelemetry();
			// A transport that failed while the control socket was down produced
			// no state change we could act on, and none is coming.
			if (ready.reattached) this.restartIce();
			return ready;
		} catch (err) {
			// Every failure path ends with the socket gone. The old handshake
			// destroyed the whole session instead; now the object survives for
			// another tap, so a retained socket would take a second
			// `session.start` the server answers with "session already started"
			// (wedging every further tap), and a retained CONNECTING one would
			// outlive the session — three taps, four half-open sockets.
			// Skipped once a newer round owns `abort` (the auto-resume after a
			// `reason:"error"` suspend), whose socket is not ours to close.
			if (this.abort === abort) this.detachSocket(true);
			throw err;
		} finally {
			if (this.abort === abort) this.abort = null;
		}
	}

	/**
	 * Whether the round holding `round` still owns the session. `abort` is the
	 * round token: a suspend or a teardown between two awaits rejects and clears
	 * it, and a newer round replaces it. Either way this round must not adopt a
	 * microphone or declare the session live — those are the two steps that
	 * outlive a failed handshake.
	 */
	private owns(round: PromiseWithResolvers<never>): boolean {
		if (this.abort !== round) return false;
		return this.phase === "new" || this.phase === "reconnecting";
	}

	/**
	 * The `session.start` for this attempt. `sessionId` names the session to
	 * re-attach to and `ack` the last sequenced frame we processed (SPEC §13);
	 * `history` rides along on every attempt so a node that cannot re-attach —
	 * the replay ring rolled over, the session was suspended, another node
	 * answered — builds the conversation from it without a second round trip.
	 *
	 * `wake` rides on exactly one kind of start: the resume the local detector
	 * triggered, which is why it is consumed here rather than read. A retry, a
	 * reconnect or a re-attach of the woken session is not itself a wake, and
	 * a start that claimed to be would silence a greeting the user is owed.
	 */
	private startFrame(token: string | undefined): SessionStart {
		const wake = this.wakeStart;
		this.wakeStart = false;
		return {
			t: "session.start",
			...(token !== undefined ? { token } : {}),
			...(this.opts.config !== undefined ? { config: this.opts.config } : {}),
			...(this.history.length > 0 ? { history: this.history } : {}),
			...(this.sessionId ? { sessionId: this.sessionId } : {}),
			...(this.sessionId && this.ack > 0 ? { ack: this.ack } : {}),
			...(wake ? { wake: true } : {}),
			...(this.tools.size > 0
				? { tools: [...this.tools.values()].map((t) => t.schema) }
				: {}),
			client: { platform: "web", version: VERSION, ua: navigator.userAgent },
		};
	}

	/**
	 * The token to put on `session.start`. A callback is awaited on every
	 * attempt, so a reconnect after a long pause fetches a fresh one rather
	 * than replaying an expired token the server would refuse.
	 */
	private async resolveToken(): Promise<string | undefined> {
		const src = this.opts.token;
		if (src === undefined) return undefined;
		const token = typeof src === "function" ? await src() : src;
		if (!token) throw new Error("no session token: the token source returned nothing");
		return token;
	}

	/**
	 * Races one handshake step against the 15 s budget and the abort signal. On
	 * either, the caller runs its failure path — mic tracks stopped,
	 * PeerConnection and socket closed — and rejects naming what never arrived.
	 */
	private deadline<T>(wait: Promise<T>, what: string): Promise<T> {
		return this.race(
			wait,
			`handshake timed out after ${HANDSHAKE_TIMEOUT_MS} ms waiting for ${what}`,
		);
	}

	/** `wait`, the 15 s budget, and the abort signal if a handshake is in flight. */
	private race<T>(wait: Promise<T>, timedOut: string): Promise<T> {
		let stop = (): void => {
			/* replaced synchronously by the executor below */
		};
		const races: Promise<T>[] = [
			wait,
			new Promise<never>((_, reject) => {
				const timer = setTimeout(() => reject(new Error(timedOut)), HANDSHAKE_TIMEOUT_MS);
				stop = () => clearTimeout(timer);
			}),
		];
		const abort = this.abort;
		if (abort) races.push(abort.promise);
		return Promise.race(races).finally(() => stop());
	}

	/** SPEC §13 auto-reconnect, on unless the caller explicitly turned it off. */
	private get autoReconnect(): boolean {
		return this.opts.autoReconnect !== false;
	}

	/**
	 * Whether this session puts the worklet graph in its send path at all.
	 *
	 * Wake needs it to hear the room while nothing is connected; the input
	 * meter needs it because the main thread never sees samples. Either reason
	 * builds the same graph. Neither, and the raw capture track goes straight
	 * into the transceiver with no `AudioContext` anywhere near the session —
	 * which is the default and the fast path, and nothing added here may
	 * change it.
	 *
	 * This is *not* the same question as "may this session arm the detector",
	 * which is `this.wake`. Everything wake-specific — arming, the pre-roll
	 * ring, payout, the drain, the context watch, retaining the capture across
	 * a suspend — stays gated on that.
	 */
	private get needsGraph(): boolean {
		return this.wake !== null || this.opts.onLevel !== undefined;
	}

	/**
	 * Applies `isMuted` wherever the audio currently is. Called from
	 * `setMuted()` and from every path that acquires a capture or a graph,
	 * because mute belongs to the session and a freshly acquired track knows
	 * nothing about it.
	 *
	 * Which mechanism is not a preference: with a graph the worklet gates its
	 * *output* and the capture tracks stay enabled, because the detector and
	 * the meter have to keep hearing the room — that is what makes wake work
	 * while muted and what lets `onLevel` report a muted user talking. With no
	 * graph the device track is what the PeerConnection sends, so disabling it
	 * is the only place to put the gate; a disabled track is exactly the same
	 * digital silence on the wire.
	 */
	private applyMute(): void {
		const graph = this.graph;
		if (graph) {
			// Only when it changes what the render thread was last told. The
			// gate a fresh worklet starts with is open, so an unmuted session
			// posts nothing on any of its legs — and a retained graph across a
			// suspend already holds the gate it was given, so a resume posts
			// nothing either.
			if (graph.muted !== this.isMuted) {
				graph.muted = this.isMuted;
				postWake(graph, { type: "mute", muted: this.isMuted });
			}
			// And the capture stays enabled, always, whatever `muted` is. This
			// is the invariant the whole design rests on: the worklet gates its
			// output, so the samples still have to arrive — a disabled track
			// under a graph would report -100 dBFS while the user talks into a
			// muted microphone, would leave an onset unheard so unmuting no
			// longer restores wake, and would keep uplinking silence after the
			// gate opened. Asserted here rather than assumed, because this is
			// the one place that knows a graph is in the path.
			for (const t of this.mic?.getTracks() ?? []) t.enabled = true;
			return;
		}
		for (const t of this.mic?.getTracks() ?? []) t.enabled = !this.isMuted;
	}

	/**
	 * One capture for a media leg, with the sticky input choice applied.
	 *
	 * A sticky `{exact}` device that can no longer be satisfied — the headset
	 * the user chose is in a drawer now, the USB microphone is on another
	 * machine — fails `getUserMedia` outright, and that must not cost the
	 * session: the choice is dropped, the failure is reported non-fatally, and
	 * the leg comes up on the system default. A refused *permission* is not a
	 * device problem and the default would be refused identically, so it is
	 * left to the caller's failure path rather than prompting twice.
	 */
	private async capture(): Promise<MediaStream> {
		const want = this.inputDeviceId;
		if (!want) return await navigator.mediaDevices.getUserMedia(MIC(null));
		try {
			return await navigator.mediaDevices.getUserMedia(MIC(want));
		} catch (err) {
			const name = err instanceof Error ? err.name : "";
			if (name === "NotAllowedError" || name === "SecurityError") throw err;
			this.inputDeviceId = null;
			this.mediaFailed(
				"audio.failed",
				`the chosen input device could not be opened, falling back to the system default: ${want}`,
				err,
			);
			return await navigator.mediaDevices.getUserMedia(MIC(null));
		}
	}

	/**
	 * Puts an already-acquired capture in the path in place of `this.mic`, and
	 * stops whatever it displaced only once the new one is carrying. Throws
	 * with the session untouched if it cannot, which is what lets
	 * `setInputDevice` promise a working session on failure.
	 *
	 * Serialized, because two swaps can be in flight at once — a picker whose
	 * `change` fires twice, or a user's `setInputDevice()` landing while the
	 * `devicechange` fallback is mid-swap. Run concurrently they would each
	 * install their own stream and each stop the one *they* saw at entry,
	 * orphaning a live capture that nothing can reach afterwards: the recording
	 * indicator lit and the device held for the life of the page, invisible to
	 * `releaseCapture()` and to `destroy()`.
	 */
	private swapCapture(next: MediaStream): Promise<void> {
		// A failed predecessor must not poison the queue: `swapping` is only
		// ever a settled-or-pending ordering token, never a result.
		const run = this.swapping.then(
			() => this.runSwap(next),
			() => this.runSwap(next),
		);
		this.swapping = run.catch(() => {
			/* every caller reports its own failure; this only keeps the order */
		});
		return run;
	}

	private async runSwap(next: MediaStream): Promise<void> {
		// Re-validated here, at the front of the queue rather than at the
		// caller's pre-check, because serializing the swaps added a window: a
		// swap can sit behind a predecessor while `destroy()` or a suspend runs
		// `releaseCapture()` underneath it. Installing the stream anyway would
		// leave a live `getUserMedia` on a session nothing can reach —
		// `destroy()` early-returns once the phase is closed, so the microphone
		// and the recording indicator would outlive the call the user hung up
		// — or re-light the indicator on the `"client"` pause they asked for.
		if (this.swapAbandoned(next)) return;
		const graph = this.graph;
		// With no graph the device track is what gets sent, so the gate goes on
		// the new tracks *before* they can be in any send path. `replaceTrack`
		// switches over on the media thread as soon as it is called and long
		// before its promise settles, so muting afterwards would put one
		// main-thread turn of real microphone audio — several 20 ms Opus
		// frames, enough to reach the STT and the transcript — on the wire for
		// a session the user has muted. Reachable without any user action at
		// all, through the `devicechange` fallback.
		//
		// With a graph the gate is the worklet's and the capture must stay
		// enabled: gating the track there would starve the detector and the
		// meter of the samples they exist to hear — a muted meter reading -100
		// dBFS, an armed detector that cannot hear the room, and silence on the
		// wire for the rest of the leg once the gate reopened over a disabled
		// source. That branch is synchronous and needs no stamp at all.
		if (!graph) {
			for (const t of next.getTracks()) t.enabled = !this.isMuted;
		}
		if (graph) {
			// A source swap in front of the worklet. The sent track is the
			// destination node's, so the PeerConnection never learns that the
			// microphone changed: no `replaceTrack`, no renegotiation, and the
			// detector's floor, the pre-roll ring and the meter's interval all
			// survive the change. The old source is disconnected before the new
			// one is wired in, not after: two sources feeding one worklet sum,
			// and a session that mixed two microphones for even a moment would
			// send the room twice.
			const src = graph.ctx.createMediaStreamSource(next);
			graph.src.disconnect();
			src.connect(graph.node);
			graph.src = src;
		} else {
			// No graph: the device track *is* the sent track. A session with no
			// PeerConnection is suspended or mid-handshake, and then swapping
			// `this.mic` is the whole of the work — the next leg builds its
			// transceiver from it.
			const sender = this.pc?.getSenders().find((s) => s.track?.kind === "audio");
			if (sender) await sender.replaceTrack(next.getAudioTracks()[0] ?? null);
		}
		// And again after the `replaceTrack` await, which is the second window
		// serializing opened: the same teardown lands here as above, and by now
		// the sender this swap fed belongs to a PeerConnection that is closed.
		if (this.swapAbandoned(next)) return;
		// Read here and not at entry: what is carrying now is what this swap
		// displaces. Anything else — a stream a concurrent swap installed and
		// has already accounted for — is not this call's to stop.
		const old = this.mic;
		this.mic = next;
		if (old && old !== next) {
			for (const t of old.getTracks()) t.stop();
		}
		// A fresh source is ungated: the worklet branch above has to be told,
		// and on the track branch this is the no-op the pre-gate above made it.
		this.applyMute();
	}

	/**
	 * Whether the session this swap was acquired for is gone: nothing is
	 * capturing (`releaseCapture` nulled it) or the session is closed. Stops
	 * the capture the swap was carrying, because past this point nothing else
	 * ever will — `destroy()` has already run and returns early on a second
	 * call, and a suspend deliberately leaves no capture behind.
	 */
	private swapAbandoned(next: MediaStream): boolean {
		if (this.mic !== null && this.phase !== "closed") return false;
		for (const t of next.getTracks()) t.stop();
		return true;
	}

	/**
	 * The sticky input device is gone. Fall back to the system default — a
	 * vanished headset must not cost the session — clear the choice so nothing
	 * keeps asking for it, and say so non-fatally.
	 */
	private async lostInput(lost: string): Promise<void> {
		this.inputDeviceId = null;
		this.mediaFailed(
			"audio.failed",
			`the chosen input device disappeared, falling back to the system default: ${lost}`,
		);
		// Nothing is capturing (suspended, or between legs): the next
		// `getUserMedia` takes the default now that the choice is cleared.
		if (!this.mic) return;
		try {
			const next = await this.capture();
			if (!this.mic || this.phase === "closed") {
				for (const t of next.getTracks()) t.stop();
				return;
			}
			await this.swapCapture(next);
		} catch (err) {
			// Both devices are unusable, which is a broken session the SDK
			// cannot repair by itself — but the server's media grace will
			// suspend it, and §13's repair is a better place to be than a
			// swallowed exception.
			this.mediaFailed("audio.failed", "the system default input device could not be opened either", err);
		}
	}

	/**
	 * `navigator.mediaDevices.devicechange`: the machine's devices changed.
	 * Re-enumerates once and hands the list to `onDevices` so a picker
	 * re-renders, then checks the one thing the SDK itself has a stake in — the
	 * device this session is capturing on. A choice that has disappeared from
	 * the list is replaced before the user notices the silence.
	 */
	private async devicesChanged(): Promise<void> {
		let list: AudioDevices;
		try {
			list = await devices();
		} catch (err) {
			this.mediaFailed("audio.failed", "the device list could not be read", err);
			return;
		}
		// Checked after the enumeration rather than before it, because that is
		// the window that matters: `destroy()` takes the listener off, so the
		// only closed session that can reach here is one that closed while the
		// list was being read, and reporting to a consumer that has finished
		// with the session is exactly what this stops.
		if (this.phase === "closed") return;
		this.opts.onDevices?.(list);
		this.deviceLabels.clear();
		for (const d of [...list.inputs, ...list.outputs]) this.deviceLabels.set(d.deviceId, d.label);
		this.telemetry?.record("device", {
			role: "list",
			inputs: list.inputs.length,
			outputs: list.outputs.length,
			...(this.inputDeviceId ? { input: this.deviceLabels.get(this.inputDeviceId) ?? "" } : {}),
			...(this.outputDeviceId
				? { output: this.deviceLabels.get(this.outputDeviceId) ?? "" }
				: {}),
		});
		const active = this.inputDeviceId;
		if (!active || list.inputs.some((d) => d.deviceId === active)) return;
		await this.lostInput(active);
	}

	/**
	 * The hidden `<audio>` element the remote track plays through, created on
	 * demand. Separate from `attachAudio` because `setOutputDevice()` can be
	 * called before the first remote track lands — a sink has to be named on
	 * an element.
	 *
	 * Creation deliberately does **not** apply the sticky sink: whoever creates
	 * the element owns naming its sink, so that exactly one `setSinkId` call is
	 * ever in flight per element. `attachAudio` re-applies the sticky choice
	 * (that is the path where an element is born without anybody asking for a
	 * sink); `setOutputDevice` awaits its own call and reports its own failure.
	 */
	private audioElement(): HTMLAudioElement {
		const have = this.audio;
		if (have) return have;
		const el = document.createElement("audio");
		el.autoplay = true;
		el.setAttribute("playsinline", ""); // iOS Safari: no fullscreen takeover
		el.style.display = "none";
		document.body.appendChild(el);
		this.audio = el;
		return el;
	}

	/**
	 * Puts a freshly created element on the sticky sink. `destroy()` removes
	 * the element, and a choice made before the first remote track landed
	 * belongs on whatever element ends up playing it, so this runs wherever an
	 * element is born under an existing choice.
	 */
	private applyStickySink(el: HTMLAudioElement): void {
		const sink = this.outputDeviceId;
		if (sink === null) return;
		// Only `ConnectOptions.outputDeviceId` can reach this branch without
		// support — `setOutputDevice` refuses outright — and a session is not
		// worth failing over a sink, so it is reported and the audio plays on
		// the default device.
		if (!outputDeviceSupported()) {
			this.mediaFailed(
				"audio.failed",
				"this browser cannot choose an audio output device, so outputDeviceId was ignored",
			);
			return;
		}
		void el.setSinkId(sink).catch((err: unknown) => {
			this.mediaFailed("audio.failed", `the output device could not be selected: ${sink}`, err);
		});
	}

	private openSocket(): Promise<void> {
		const open = Promise.withResolvers<void>();
		const ws = new WebSocket(this.opts.url);
		this.ws = ws;
		ws.onopen = () => open.resolve();
		ws.onmessage = (ev) => this.onMessage(ev.data);
		ws.onerror = () => {
			/* onclose always follows; failures are reported there */
		};
		ws.onclose = (ev) => {
			if (this.ws !== ws) return; // a socket already detached or replaced
			this.ws = null;
			const err = new Error(
				`control socket closed (${ev.code}${ev.reason ? `: ${ev.reason}` : ""})`,
			);
			open.reject(err);
			this.socketGone(err);
		};
		return open.promise;
	}

	/**
	 * The control socket died. That is not the session dying: SPEC §13 parks the
	 * session on the node with its media live for a grace window, so the client
	 * keeps its own media leg too and re-binds only the control channel.
	 */
	private socketGone(err: Error): void {
		switch (this.phase) {
			case "closed":
			case "suspended":
				// Nothing is holding this socket. A later resume() opens its own.
				return;
			case "new":
				// The first handshake never came up: there is no live session to
				// re-attach to, so connect() must reject rather than retry into
				// a server that may be refusing this client outright.
				this.fail(err);
				return;
			case "suspending":
				// The socket carrying `session.suspend` died before the answer.
				// Reconnecting would re-attach the session the user just asked to
				// release; the node parks and then reaps it on its own, so the
				// suspend completes locally and drops the re-attach identity with
				// it — see suspend()'s no-socket branch for why keeping it is
				// worse than a fresh session.
				this.sessionId = "";
				this.ack = 0;
				this.enterSuspended("client");
				return;
			case "reconnecting":
				// An attempt is in flight; rejecting its waiters is what makes it
				// reschedule (or, for a resume(), report to its caller).
				this.rejectWaiters(err);
				return;
			case "live":
				this.rejectWaiters(err);
				if (!this.autoReconnect || !this.sessionId) {
					this.fail(err);
					return;
				}
				// SPEC §13 keeps the media leg across the gap, so a duck taken
				// just before the socket died would otherwise hold until the
				// re-attached socket happened to carry a frame that lifts it.
				// The audio is still flowing; nothing is coming to un-mute it.
				this.duck(false, "reset");
				this.phase = "reconnecting";
				this.scheduleReconnect();
				return;
		}
	}

	/**
	 * SPEC §13 backoff: 250 ms doubling to 5 s, a fresh token and a fresh
	 * `session.start{sessionId, ack, history}` per attempt. Ten consecutive
	 * failures mean the node is not coming back within any window that could
	 * still re-attach, so the session ends rather than retrying forever behind
	 * a status the user cannot act on.
	 */
	private scheduleReconnect(): void {
		if (this.phase === "closed" || this.reconnectTimer) return;
		if (this.attempts >= RECONNECT_TRIES) {
			// The real reason, not the subsystem: ten silent attempts behind a
			// 401 from the token endpoint used to be reported as a lost socket.
			const last = this.lastError?.message ?? "control socket lost";
			this.reportError({
				t: "error",
				code: "reconnect.failed",
				message: `${RECONNECT_TRIES} reconnect attempts failed: ${last}`,
				fatal: true,
			});
			this.destroy();
			return;
		}
		const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.attempts);
		this.attempts++;
		this.telemetry?.record("reconnect", {
			attempt: this.attempts,
			reason: this.lastError?.message ?? "control socket lost",
		});
		this.phase = "reconnecting";
		this.setState("reconnecting");
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			void this.reconnect();
		}, delay);
	}

	private async reconnect(): Promise<void> {
		// A resume() that started while this attempt was on the clock owns the
		// socket now; two handshakes on one session would race for `waitReady`.
		if (this.phase !== "reconnecting" || this.resuming) return;
		try {
			await this.handshake();
		} catch (err) {
			if (this.phase !== "reconnecting") return;
			// Kept for the give-up frame: whatever failed — the socket, the
			// token mint, `session.ready` — is what the app needs to be told
			// after the last attempt. runHandshake already dropped the socket.
			this.lastError = err instanceof Error ? err : new Error(String(err));
			this.scheduleReconnect();
		}
	}

	private clearReconnect(): void {
		if (!this.reconnectTimer) return;
		clearTimeout(this.reconnectTimer);
		this.reconnectTimer = null;
	}

	/**
	 * Builds the media leg: the capture — or the audio graph already held — the
	 * PeerConnection, and one offer/answer round.
	 */
	private async negotiateMedia(
		iceServers: RTCIceServer[],
		round: PromiseWithResolvers<never>,
	): Promise<void> {
		// A resume, or a re-attach the node refused, must never leave the old
		// PeerConnection alive behind the new one.
		this.dropPeer();
		// A retained graph needs one thing checked before it is adopted: its
		// context. Mobile Safari suspends one while the tab is hidden, and a
		// stopped worklet feeds a live leg exact zeros — `TODO.md` 29
		// manufactured on purpose. One that will not resume is not repairable
		// in place, so it is dropped here and this leg acquires a fresh one
		// below, permission prompt and all.
		if (this.graph) await this.adoptWakeContext();
		let mic: MediaStream | null = null;
		let graph: AudioGraph | null = null;
		if (!this.graph) {
			// No graph to adopt, so whatever capture the last leg had is this
			// leg's to replace: leaving it running would light a second
			// recording indicator behind the new one.
			this.releaseCapture();
			mic = await this.capture();
			if (this.needsGraph) {
				try {
					graph = await this.buildAudioGraph(mic);
				} catch (err) {
					// The consumer asked for wake, or for a meter, and is not
					// going to get it — a blocked worklet asset, a context the
					// platform refused. Report it and bring the session up on
					// the device track anyway: a CSP typo must not cost the
					// call, and with no graph nothing arms, nothing is retained
					// and no level is reported. The next media leg tries again,
					// and reports again, because a worklet that failed to fetch
					// once is not a worklet that can never be fetched — the
					// cost of the retry is bounded by real resumes, not by
					// onsets.
					this.graphFailed(
						this.wake
							? "the local speech detector could not start"
							: "the input level meter could not start",
						err,
					);
				}
			}
		}
		// None of the awaits above can be raced against the abort signal, on
		// either branch. A `session.suspended` (the idle rule, or the server's
		// 10 s media grace) landing while the permission prompt is up — or
		// while a hidden tab's `AudioContext` is resuming — has already run
		// `dropPeer()`, so going on from here would adopt a capture that leaves
		// the recording indicator lit and build a second PeerConnection that
		// offers into a session the user is shown as suspended, holding a
		// sender and gathering ICE until the 15 s `rtc.answer` deadline. A
		// retained capture is the session's, not this round's, and is left for
		// `enterSuspended` to have decided about.
		if (!this.owns(round)) {
			releaseAudioGraph(graph);
			for (const t of mic?.getTracks() ?? []) t.stop();
			throw new Error("handshake abandoned: the session was released");
		}
		if (mic) {
			this.mic = mic;
			this.graph = graph;
		}
		// Mute is re-applied on every leg, adopted graph or fresh capture: a
		// capture just acquired has enabled tracks, a graph just built has an
		// open gate, and a retained graph is re-told what it already knows for
		// the price of one message. This is the join point every media leg goes
		// through — first connect, resume, each reconnect attempt, and the
		// rebuild after a graph that would not be adopted — so mute surviving
		// all of them is this one line and not four.
		this.applyMute();
		// The context watch is the graph's, installed once per graph and dropped
		// with it in `releaseCapture`. A platform that suspends an
		// `AudioContext` mid-call — mobile Safari on a hidden tab — stops the
		// worklet, and the destination node then carries exact zeros to the
		// server for the rest of the leg with nothing to bring it back. That is
		// as true of a session that only draws a meter as of one that arms a
		// detector, so it cannot be the arm that installs this.
		if (this.graph) this.watchAudioContext(this.graph);
		await this.negotiate(this.startMedia(iceServers), false);
	}

	/**
	 * One offer/answer round on `pc`: the initial negotiation and every ICE
	 * restart. The answer waiter and the queued remote candidates are therefore
	 * per negotiation, not per session — candidates that arrive before *this*
	 * answer are applied after it, and the queue starts empty again.
	 */
	private async negotiate(pc: RTCPeerConnection, iceRestart: boolean): Promise<void> {
		const wait = Promise.withResolvers<string>();
		this.waitAnswer = wait;
		const offer = iceRestart
			? await pc.createOffer({ iceRestart: true })
			: await pc.createOffer();
		const sdp = mungeOpus(offer.sdp ?? "");
		await pc.setLocalDescription({ type: "offer", sdp });
		this.send({ t: "rtc.offer", sdp });
		const answer = await this.deadline(wait.promise, "rtc.answer");
		await pc.setRemoteDescription({ type: "answer", sdp: answer });
		for (const c of this.pendingIce.splice(0)) await pc.addIceCandidate(c);
	}

	private startMedia(iceServers: RTCIceServer[]): RTCPeerConnection {
		// With a graph in the path (wake, a meter, or both) the worklet is
		// always in it (§5): one code path, one AEC story, no `replaceTrack` at
		// a wake and none at a device switch either. AEC runs inside the
		// capture, so the destination node's track is post-AEC just the same,
		// and Opus is pinned at the SDP level either way.
		const track = this.graph
			? this.graph.dest.stream.getAudioTracks()[0]
			: this.mic?.getAudioTracks()[0];
		if (!track) {
			throw new Error(this.graph ? "no audio graph output track" : "no microphone track");
		}
		const pc = new RTCPeerConnection({ iceServers });
		this.pc = pc;
		pc.addTransceiver(track, { direction: "sendrecv" });

		pc.onicecandidate = (ev) => {
			if (ev.candidate) {
				this.send({ t: "rtc.ice", candidate: ev.candidate.toJSON() });
			}
		};
		pc.ontrack = (ev) => {
			// Playout edges, the cheap half (SPEC §12). A remote track is muted
			// by spec whenever it is not receiving data — but "receiving data"
			// is not "audible", and against *this* server the two are never the
			// same thing: egress writes a silent Opus packet every 20 ms so the
			// RTP timestamp never stalls (internal/transport/webrtc.go, pump),
			// so the track unmutes once at leg-up and never mutes again.
			// Treating that as a burst put a `playout_start` in every trace
			// with nothing playing — measured, on a live node, as a start at
			// leg-up and the energy poll answering −90.3 dBFS 450 ms later.
			// So `unmute` only *arms* the poll; what declares a burst is the
			// energy in `foldPlayout`. `mute` is sound the other way round — no
			// packets is no audio, whatever the last sample said — so it still
			// closes a burst, which is the free half for a server that does
			// stop sending between turns.
			// Guarded the way the `devicechange` listener is: a remote track
			// without the EventTarget half is a test double, and it must not
			// cost the media leg.
			ev.track.addEventListener?.("unmute", () => this.startPlayoutPoll());
			ev.track.addEventListener?.("mute", () => this.playoutEdge(false, "track"));
			this.applyJitterTarget();
			this.attachAudio(ev.streams[0] ?? new MediaStream([ev.track]));
		};
		pc.onconnectionstatechange = () => {
			if (this.pc !== pc) return; // an old leg finishing its teardown
			this.telemetry?.record("ice", { state: pc.connectionState });
			switch (pc.connectionState) {
				case "connected":
					this.clearIceTimer();
					this.iceAttempts = 0;
					// The leg carries audio now, which is the first moment a
					// pre-roll payout would not be played into nothing.
					this.wakeConnected();
					break;
				case "failed":
					// A failed transport never heals by itself (SPEC §13).
					this.restartIce();
					break;
				case "disconnected":
					// Usually a blip that heals; 2 s is long enough to tell, and
					// short enough to beat the server's 10 s media grace.
					this.armIceRestart(ICE_DISCONNECT_GRACE_MS);
					break;
				default:
					break;
			}
		};
		return pc;
	}

	private armIceRestart(delay: number): void {
		if (this.iceTimer || this.restarting) return;
		this.iceTimer = setTimeout(() => {
			this.iceTimer = null;
			this.restartIce();
		}, delay);
	}

	private clearIceTimer(): void {
		if (!this.iceTimer) return;
		clearTimeout(this.iceTimer);
		this.iceTimer = null;
	}

	/**
	 * ICE restart (SPEC §13): a new offer with `iceRestart` on the same
	 * PeerConnection, so the session, its transcript, its jobs and the turn in
	 * flight all survive a network change — nothing here touches the session.
	 * Three attempts; after that the server's own 10 s media grace suspends
	 * with `reason:"error"` and the SDK resumes from `history`, which is the
	 * only repair left.
	 */
	private restartIce(): void {
		this.clearIceTimer();
		const pc = this.pc;
		if (!pc || this.restarting || this.phase !== "live") return;
		if (pc.connectionState !== "failed" && pc.connectionState !== "disconnected") return;
		if (this.iceAttempts >= ICE_RESTART_TRIES) {
			this.iceLost();
			return;
		}
		this.iceAttempts++;
		this.restarting = true;
		void this.negotiate(pc, true).then(
			() => {
				this.restarting = false;
				// The receiver survives an ICE restart, but a browser that
				// rebuilt its jitter buffer under the new transport has to be
				// told the target again; re-applying an unchanged value costs
				// one assignment.
				this.applyJitterTarget();
				// Whether it took is the next connectionstatechange's answer.
			},
			() => {
				this.restarting = false;
				if (this.pc !== pc) return; // the media leg was rebuilt underneath us
				if (this.iceAttempts >= ICE_RESTART_TRIES) this.iceLost();
				else this.armIceRestart(0);
			},
		);
	}

	/**
	 * Pins `RTCRtpReceiver.jitterBufferTarget` on the audio receiver, when
	 * `ConnectOptions.jitterBufferTarget` asked for one. Chromium-only: the
	 * attribute simply does not exist elsewhere, and assigning to a property
	 * no implementation reads is the documented no-op.
	 */
	private applyJitterTarget(): void {
		const opt = this.opts.jitterBufferTarget;
		if (opt === undefined || !Number.isFinite(opt)) return;
		// Clamped rather than trusted: the attribute throws `RangeError`
		// outside [0, 4000], and this runs inside `ontrack` one line ahead of
		// `attachAudio` — an out-of-range option would cost the session every
		// bit of its remote audio, not just its jitter target. Same shape as
		// the constructor's clamp on `levelIntervalMs`.
		const target = Math.min(JITTER_TARGET_MAX_MS, Math.max(0, opt));
		for (const r of this.pc?.getReceivers?.() ?? []) {
			if (r.track?.kind !== "audio") continue;
			// Not in the DOM lib this project builds against; `undefined` on a
			// browser without it, which is exactly the feature test.
			const tunable = r as RTCRtpReceiver & { jitterBufferTarget?: number | null };
			if (!("jitterBufferTarget" in tunable)) continue;
			tunable.jitterBufferTarget = target;
			// `targetMs`, not `ms`: the trace splices an event's own fields into
			// its line, and `ms` there is the session clock — an event carrying
			// one re-dates itself to 150 ms into the session.
			this.telemetry?.record("jitter_target", { targetMs: target });
		}
	}

	/**
	 * Restarts are spent. Reported once — the counter itself is the latch, so
	 * the `failed` events that keep arriving until the server suspends do not
	 * turn one dead transport into a stream of errors.
	 */
	private iceLost(): void {
		if (this.iceAttempts > ICE_RESTART_TRIES) return;
		this.iceAttempts = ICE_RESTART_TRIES + 1;
		this.reportError({
			t: "error",
			code: "rtc.failed",
			message: "media connection failed",
			fatal: true,
		});
	}

	private attachAudio(stream: MediaStream): void {
		// Whether this call is the one that creates the element decides whether
		// the sticky sink is this call's to apply: an element that already
		// exists is already on whatever sink was last named.
		const born = this.audio === null;
		const el = this.audioElement();
		if (born) this.applyStickySink(el);
		el.srcObject = stream;
		// The render graph is the sink that can be faded; the element is the
		// fallback and, while the graph plays, a silent second holder of the
		// same track. Built before `play()` so a context that is already
		// running mutes the element in the same task and the two never both
		// make sound.
		this.openOutput(stream);
		void el.play().catch(() => {
			/* autoplay blocked until a gesture; connect() is gesture-driven */
		});
	}

	/**
	 * Routes the remote stream through `source → gain → destination` on a
	 * context of the session's own, so the output has a gain to fade.
	 *
	 * The element stays attached to the same stream: it is what plays where
	 * this cannot (no `AudioContext`, a context the autoplay policy will not
	 * start, a context mobile Safari suspended on a hidden tab), and it is the
	 * element `setSinkId` names. Exactly one of the two is audible at a time —
	 * see `syncOutputRoute`.
	 *
	 * Idempotent per stream: a second media leg re-points the source at the
	 * new stream and keeps the context, the gain and the autoplay permission
	 * the first one earned.
	 */
	private openOutput(stream: MediaStream): void {
		if (typeof AudioContext !== "function") return;
		try {
			let ctx = this.outCtx;
			if (!ctx) {
				// Its own context, not the capture graph's: that one exists
				// only where `needsGraph` does (wake, or a meter), it is
				// pinned to 48 kHz, and `releaseCapture()` closes it on every
				// suspend and every device swap — which would take the remote
				// audio down with the microphone. No `sampleRate` here: the
				// output should run at the device's own rate, and pinning one
				// would insert a resampler in front of the speaker for
				// nothing.
				ctx = new AudioContext({ latencyHint: "interactive" });
				this.outCtx = ctx;
				ctx.addEventListener?.("statechange", this.onOutState);
				const sink = this.outputDeviceId;
				if (sink !== null) {
					void this.nameOutputSink(sink).catch((err: unknown) => {
						this.mediaFailed(
							"audio.failed",
							`the output device could not be selected: ${sink}`,
							err,
						);
					});
				}
			}
			const gain = this.outGain ?? ctx.createGain();
			this.outGain = gain;
			// The duck outlives a re-attach: the gain is re-stamped rather than
			// assumed, so a leg rebuilt while the output is down comes back
			// down and a leg rebuilt while it is up comes back up.
			gain.gain.value = this.ducked && !this.bed ? 0 : 1;
			this.outSrc?.disconnect();
			const src = ctx.createMediaStreamSource(stream);
			this.outSrc = src;
			src.connect(gain);
			gain.connect(ctx.destination);
		} catch (err) {
			this.releaseOutput();
			this.reportOutputDeaf("the remote audio could not be routed for fading", err);
			return;
		}
		// `resume()` is a promise the autoplay policy may reject, and a context
		// that is merely not running *yet* is not one that will not start:
		// Safari and iOS Safari construct every context suspended, and
		// Chromium does too before the document has an activation. So the
		// route is re-decided when the state actually lands, and only the
		// rejection is worth telling the consumer about.
		void this.outCtx?.resume().then(
			() => this.syncOutputRoute(),
			(err: unknown) => {
				this.syncOutputRoute();
				this.reportOutputDeaf(
					"the audio output context would not start, so playback cannot be faded",
					err,
				);
			},
		);
		this.syncOutputRoute();
	}

	/**
	 * Asks a suspended output context to come back. Mobile Safari suspends a
	 * hidden tab's context and never resumes it on its own, and the only other
	 * `resume()` here belongs to a new media leg — so without this a session
	 * that spent a moment in the background would play out of the element, a
	 * step for every cut, for the rest of the call. Cannot loop: a refused
	 * resume produces no further `statechange`.
	 */
	private wakeOutput(): void {
		const ctx = this.outCtx;
		if (ctx?.state !== "suspended") return;
		void ctx.resume().catch(() => {
			/* it stays on the element route, which is at least audible */
		});
	}

	/**
	 * Picks which of the two sinks is audible. The render graph wins whenever
	 * its context is running, because it is the only one a fade can be
	 * scheduled on; the element takes over the moment it is not, because
	 * silence is worse than a cut that clicks.
	 */
	private syncOutputRoute(): void {
		const ctx = this.outCtx;
		const running = ctx?.state === "running" && this.outGain !== null;
		const el = this.audio;
		// `|| this.ducked`: the element is the audible route when the graph is
		// not running, and a ducked one must stay silent through the change.
		if (el) el.muted = running || this.ducked;
		// No report here: a context is routinely suspended for a moment before
		// its own `resume()` settles, and saying so would burn the one-shot
		// latch that a context which genuinely never starts needs.
		if (this.elementOut === !running) return;
		this.elementOut = !running;
		// Whichever object is making sound now has to carry the current duck:
		// the two gains are independent, and a route change under a duck would
		// otherwise un-mute the liaison the user is talking over.
		this.applyDuck(false);
		this.telemetry?.record("output", { route: running ? "graph" : "element" });
	}

	/** One report per session for an output that cannot fade. Never fatal. */
	private reportOutputDeaf(message: string, err?: unknown): void {
		if (this.outDeaf) return;
		this.outDeaf = true;
		this.mediaFailed("audio.failed", message, err);
	}

	/**
	 * Names a sink on the output context, where the browser implements one
	 * (`AudioContext.setSinkId`, Chromium 110+). Awaited by
	 * `setOutputDevice()`, because on the graph route this call — not the
	 * element's — is the one that moves the audio.
	 *
	 * Where it is missing, the graph is given up and the element takes the
	 * audio back — its own `setSinkId` is older and wider, and a user who
	 * picked a headset must not be answered on the laptop speaker because the
	 * SDK wanted a fade. Unless the element cannot name a sink either (Safari,
	 * Firefox), where giving the graph up would cost the fade and buy nothing:
	 * the choice is unreachable on that browser and `applyStickySink` has
	 * already said so.
	 */
	private async nameOutputSink(sink: string | null): Promise<void> {
		const ctx = this.outCtx;
		if (!ctx) return;
		// `AudioContext.setSinkId` is Chromium 110+; the DOM lib this project
		// builds against does not declare it, and there is nothing to validate
		// — its presence *is* the feature test below.
		const sinkable = ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
		if (typeof sinkable.setSinkId !== "function") {
			if (!outputDeviceSupported()) return;
			this.releaseOutput();
			this.reportOutputDeaf(
				"this browser cannot name an output device on an AudioContext, so the remote audio plays through the element and cannot be faded",
			);
			return;
		}
		// `""` is the spec's own spelling of the default sink, on both objects.
		await sinkable.setSinkId(sink ?? "");
	}

	/** Drops the whole render graph, and with it the ability to fade. */
	private releaseOutput(): void {
		this.outSrc?.disconnect();
		this.outSrc = null;
		this.outGain?.disconnect();
		this.outGain = null;
		const ctx = this.outCtx;
		this.outCtx = null;
		if (!ctx) return;
		ctx.removeEventListener?.("statechange", this.onOutState);
		void ctx.close().catch(() => {
			/* already closed, or a context the page tore down under us */
		});
		this.syncOutputRoute();
	}

	/**
	 * Fades the output down or back up (SPEC §6.3, `playback.duck`).
	 *
	 * This is the whole of the client's half of a barge-in. By the time the
	 * server decides to stop talking, between 450 ms (LAN) and 1150 ms (WAN)
	 * of its voice is already inside the receiver's jitter buffer and past
	 * everything either side can reach; the only cut that happens *now* is one
	 * made at the last hop before the speaker. Measured on ten traced
	 * sessions, that buffer was 855 ms local / 1391 ms remote of the
	 * onset→silence time, more than half of it.
	 *
	 * `via` records what moved it, because the three paths fail differently:
	 * `"frame"` is the server pausing egress, `"clear"` a confirmed interrupt,
	 * `"state"` the belt that lifts a clear's duck when the next turn starts
	 * speaking, and `"reset"` a teardown refusing to leave one stuck.
	 */
	private duck(on: boolean, via: "frame" | "clear" | "state" | "reset"): void {
		if (this.ducked === on) {
			// A clear over a duck the server had already asked for takes
			// ownership of the un-duck: there is no `on:false` coming for a
			// confirmed interrupt, so the belt has to be armed.
			if (on && via === "clear") this.duckHold = "clear";
			return;
		}
		// The output context keeps advancing while its gain is zero. Commit the
		// last newly audible position before the fade begins; the ordinary
		// cadence must not hide the terminal edge. The shared finalizer also
		// preserves a greater timestamp already observed if this clock regressed.
		if (on && this.playingOut) this.finishPlayoutClock();
		this.ducked = on;
		this.duckHold = on ? (via === "clear" ? "clear" : "frame") : null;
		this.applyDuck(true);
		if (!on && this.playingOut) this.outputBurstAt = this.outputClockSamples();
		this.telemetry?.record("duck", { on, via });
	}

	/**
	 * Puts the current duck on whatever is playing. `fade` off is a re-stamp —
	 * a new media leg, a route change — where there is no continuity to
	 * preserve and a ramp would only delay the value.
	 */
	private applyDuck(fade: boolean): void {
		const to = this.ducked ? 0 : 1;
		// The element is the fallback route and has no sample-accurate gain:
		// there the cut is a step, and the click the fade exists to avoid
		// comes back. `muted` as well as `volume`, because iOS keeps the
		// output level under the user's physical control and ignores `volume`
		// outright — and an iPhone whose context Safari suspended is exactly
		// the session left on this route. Harmless while the graph plays: the
		// element is muted then either way.
		const el = this.audio;
		if (el) {
			el.volume = to;
			el.muted = !this.elementOut || this.ducked;
		}
		const gain = this.outGain;
		const ctx = this.outCtx;
		if (!gain || !ctx) return;
		const p = gain.gain;
		const now = ctx.currentTime;
		// `cancelAndHoldAtTime` is what keeps a reversal smooth: plain
		// `cancelScheduledValues` on a curve still running snaps the parameter
		// back to the value it held before the curve was scheduled, which is
		// the click again.
		if (typeof p.cancelAndHoldAtTime === "function") p.cancelAndHoldAtTime(now);
		else p.cancelScheduledValues(now);
		const from = p.value;
		if (this.bed && this.ducked) {
			// The pulse's resting value is 1: a re-stamp must not silence the
			// bed for the rest of the duck. The element route has no schedule,
			// so there the duck stays a held mute.
			if (!fade || this.elementOut) {
				p.value = 1;
				return;
			}
			const fadeS = DUCK_FADE_MS / 1000;
			p.setValueAtTime(from, now);
			p.setValueCurveAtTime(fadeCurve(from, 0), now, fadeS);
			p.setValueCurveAtTime(fadeCurve(0, 1), now + fadeS + duckPulseHoldMs(this.jitterMs) / 1000, fadeS);
			return;
		}
		if (!fade || this.elementOut || from === to) {
			p.value = to;
			return;
		}
		p.setValueAtTime(from, now);
		p.setValueCurveAtTime(fadeCurve(from, to), now, DUCK_FADE_MS / 1000);
	}

	/**
	 * Writes one client frame, or holds it for the re-attached socket.
	 *
	 * This is also where a `context.inject` becomes a `history` row. The server
	 * persists the note (`role:"context"`, rendered to the model as a user turn
	 * marked `[context]`) but answers no transcript frame for it, so the client
	 * has to record its own — `history` is the durable conversation (SPEC §5.3)
	 * and a note missing from it is a note the next resume never sends. The row
	 * goes in *here*, at the moment the frame reaches the wire, rather than in
	 * `inject()`: that is the same moment the server appends its own, so the
	 * two orderings agree, a note held through a §13 gap is recorded when
	 * `flushPending` finally writes it, and a note the socket never carried —
	 * dropped by `hold`, or held into a suspend that clears `pending` — leaves
	 * no row behind. Rows that arrived in the resumed `history` are not
	 * re-recorded: nothing here reads the `session.start` it just sent.
	 */
	private send(f: ClientFrame): void {
		const ws = this.ws;
		if (!ws || ws.readyState !== WebSocket.OPEN) {
			this.hold(f);
			return;
		}
		ws.send(JSON.stringify(f));
		if (f.t === "context.inject") {
			this.history.push({ role: "context", text: f.text, ts: Date.now() });
		}
		this.opts.onFrame?.("out", tappedFrame(f));
	}

	/**
	 * Writes ephemeral playout progress only while this socket is synchronously
	 * bound to the live session. Unlike ordinary frames it must never enter the
	 * reconnect queue: the next measurement retries the same high-water.
	 */
	private sendPlayoutPosition(playedSamples: number): boolean {
		const ws = this.ws;
		if (
			this.phase !== "live" ||
			!this.sessionId ||
			!ws ||
			ws.readyState !== WebSocket.OPEN
		)
			return false;
		try {
			ws.send(JSON.stringify({ t: "playback.position", playedSamples }));
		} catch {
			return false;
		}
		this.opts.onFrame?.("out", { t: "playback.position", playedSamples });
		return true;
	}

	/**
	 * Holds a frame the socket could not carry, for the re-attached one. Worth
	 * holding only while there is a live session to re-attach to: a suspended
	 * or closed session took its jobs with it, and a session that was never
	 * named cannot be re-attached at all, so those frames have nowhere to land.
	 */
	private hold(f: ClientFrame): void {
		if (!RESENDABLE[f.t] || !this.sessionId) return;
		if (this.phase !== "live" && this.phase !== "reconnecting") return;
		if (this.pending.length >= PENDING_MAX) return;
		this.pending.push(f);
	}

	/** Writes the held frames on the re-attached socket, in the order they were made. */
	private flushPending(): void {
		for (const f of this.pending.splice(0)) this.send(f);
	}

	private onMessage(data: unknown): void {
		if (typeof data !== "string") return;
		let f: ServerFrame;
		try {
			f = JSON.parse(data) as ServerFrame;
		} catch {
			return;
		}
		switch (f.t) {
			case "session.ready": {
				this.bed = f.bed === true;
				this.playoutOnDb =
					typeof f.bedDb === "number" ? Math.max(PLAYOUT_ON_DB, f.bedDb + PLAYOUT_BED_MARGIN_DB) : PLAYOUT_ON_DB;
				this.waitReady?.resolve(f);
				this.waitReady = null;
				break;
			}
			case "rtc.answer": {
				this.waitAnswer?.resolve(f.sdp);
				this.waitAnswer = null;
				break;
			}
			case "rtc.ice": {
				const pc = this.pc;
				if (pc?.remoteDescription) void pc.addIceCandidate(f.candidate);
				else this.pendingIce.push(f.candidate);
				break;
			}
			case "state": {
				// SPEC §7.2: an absent `running` means nothing is running.
				this.jobs = f.running ?? [];
				this.agent = f.agent;
				// The belt (SPEC §6.3): a confirmed interrupt ducks and is never
				// told to stop — the server resumes with the next turn's audio,
				// not with an `on:false` — so the next turn starting to speak is
				// what lifts it. A duck the server is holding deliberately
				// (`duckHold === "frame"`) is left alone: the state does not
				// change while it speaks over itself, and lifting one here would
				// un-mute the liaison the user is talking over.
				if (f.state === "speaking" && this.duckHold === "clear") this.duck(false, "state");
				this.setState(f.state);
				break;
			}
			case "transcript.user": {
				if (f.final) {
					// A re-attach replays the finals past our `ack` (SPEC §13) and
					// the ring may hand back one we already have: the turn is
					// keyed by `messageId`, so a repeat corrects that turn in
					// place instead of adding a second row to the history the
					// next resume hands back.
					const known = f.messageId ? this.userTurns.get(f.messageId) : undefined;
					if (known) {
						known.text = f.text;
						if (f.lang) known.lang = f.lang;
					} else {
						const m: SpeechMessage = {
							role: "user",
							text: f.text,
							...(f.lang ? { lang: f.lang } : {}),
							ts: Date.now(),
						};
						this.history.push(m);
						if (f.messageId) this.userTurns.set(f.messageId, m);
					}
					// The woken turn committed (§5): the wake was real, so the
					// no-commit backoff resets, and (§6) its pre-roll backlog
					// may start draining once the false-cut window has passed.
					// Gated on the wake's own flags rather than on the frame: a
					// re-attach replays finals and `known` above absorbs them,
					// so this arm has to be idempotent.
					if (this.woken) {
						this.wakeCommitted = true;
						this.wakeMisses = 0;
					}
					if (this.draining) this.armDrain();
				}
				this.telemetry?.record("transcript", {
					role: "user",
					final: true,
					...(f.messageId ? { messageId: f.messageId } : {}),
				});
				this.opts.onTranscript({
					role: "user",
					text: f.text,
					final: f.final,
					...(f.lang ? { lang: f.lang } : {}),
					...(f.final && f.messageId ? { messageId: f.messageId } : {}),
				});
				break;
			}
			case "transcript.user.amend": {
				// The turn is unknown to a client that reconnected or joined late;
				// there is nothing on screen to correct, so drop it silently.
				const turn = this.userTurns.get(f.messageId);
				if (!turn) break;
				turn.text = f.text;
				this.telemetry?.record("transcript", {
					role: "user",
					final: true,
					amend: true,
					messageId: f.messageId,
				});
				this.opts.onTranscript({
					role: "user",
					text: f.text,
					final: true,
					messageId: f.messageId,
					amend: true,
					...(turn.lang ? { lang: turn.lang } : {}),
				});
				break;
			}
			case "transcript.assistant": {
				const prev = this.assistant.get(f.messageId);
				if (prev) {
					prev.text = f.text;
					// The server's own prefix grows with the turn (a repeated
					// filler), so it is replaced rather than set once.
					if (f.fixed) prev.fixed = f.fixed;
					if (f.source) prev.source = f.source;
					if (f.jobId) prev.jobId = f.jobId;
					if (f.interrupted) prev.interrupted = true;
				} else {
					const m: SpeechMessage = { role: "assistant", text: f.text, ts: Date.now() };
					if (f.fixed) m.fixed = f.fixed;
					if (f.source) m.source = f.source;
					if (f.jobId) m.jobId = f.jobId;
					if (f.interrupted) m.interrupted = true;
					this.assistant.set(f.messageId, m);
					this.history.push(m);
				}
				this.telemetry?.record("transcript", {
					role: "assistant",
					messageId: f.messageId,
					final: f.done,
					spokenChars: f.spokenChars,
				});
				this.opts.onTranscript({
					role: "assistant",
					messageId: f.messageId,
					text: f.text,
					spokenChars: f.spokenChars,
					done: f.done,
					...(f.interrupted ? { interrupted: true } : {}),
					...(f.source ? { source: f.source } : {}),
					...(f.jobId ? { jobId: f.jobId } : {}),
				});
				break;
			}
			case "transcript.context": {
				this.recordContext(f);
				break;
			}
			case "tool.call": {
				this.runTool(f);
				break;
			}
			case "tool.cancel": {
				// The job is already gone server-side: abort the handler and
				// answer nothing.
				this.abortJob(f.id);
				break;
			}
			case "transcript.tool": {
				this.recordTool(f);
				break;
			}
			case "session.suspended": {
				this.enterSuspended(f.reason);
				break;
			}
			case "session.ended": {
				this.destroy();
				break;
			}
			case "playback.clear": {
				// The clear frees nothing by itself: the server has dropped its
				// own ring, but the audio the user is still hearing is inside
				// the receiver's jitter buffer, which no frame can reach. What
				// makes the cut audible is the fade — and the un-duck is the
				// next turn's `speaking`, because a confirmed interrupt gets no
				// `playback.duck{on:false}`.
				this.telemetry?.record("clear");
				this.duck(true, "clear");
				this.opts.onPlaybackClear?.(f);
				break;
			}
			case "playback.duck": {
				// Not consumer-visible: the server pausing and resuming its own
				// egress around a speech run that may or may not become an
				// interrupt is the SDK's business, and a page that redrew on it
				// would flicker on every "mm-hm".
				this.duck(f.on, "frame");
				break;
			}
			case "error": {
				this.reportError(f);
				const err = new Error(`${f.code}: ${f.message}`);
				// A fatal error is the server refusing this client, not a lost
				// socket: reconnecting into it would only repeat the refusal.
				if (f.fatal) {
					this.fail(err);
					break;
				}
				// A refused renegotiation (`internal_error: renegotiation
				// failed`) is the answer to the offer we are waiting on, and the
				// frames carry no correlation id to prove it. Failing the wait on
				// any non-fatal error costs an unrelated error one ICE attempt;
				// waiting for the deadline instead costs 15 s per attempt, so
				// three attempts never finish inside the server's 10 s media
				// grace and §13's client-side repair never gets its third try.
				this.waitAnswer?.reject(err);
				this.waitAnswer = null;
				// A non-fatal error during the handshake (e.g. `not_implemented`,
				// or `protocol_error: session already started`) still means this
				// session will never come up: abandon the round now instead of
				// waiting out the handshake budget.
				this.abort?.reject(err);
				break;
			}
			default:
				break;
		}
		// The replay ring is keyed by these (SPEC §13), so the ack advances only
		// after the frame was applied: a throw above must not tell the server we
		// have a frame we never folded in. Unsequenced frames carry none.
		if ("seq" in f && typeof f.seq === "number" && f.seq > this.ack) this.ack = f.seq;
		// The tap fires after the frame was applied, so a consumer reading
		// `s.jobs`, `s.state` or `s.history` from it sees the frame it was just
		// handed — a `state` carrying `running` is the only signal a job
		// started or ended (SPEC §9), and a tap ahead of the switch would
		// answer with the previous snapshot.
		this.opts.onFrame?.("in", f);
	}

	// ----------------------------------------------------------------- tool jobs

	/**
	 * Runs one client tool (SPEC §9). The call's `id` is the job id, so a frame
	 * replayed after a reconnect (SPEC §13) names a job that already ran and is
	 * dropped. A name this client does not implement is answered with an error
	 * rather than left to time out on the server.
	 */
	private runTool(f: ToolCall): void {
		if (this.served.has(f.id)) return;
		this.served.add(f.id);

		const tool = this.tools.get(f.name);
		if (!tool) {
			this.send({ t: "tool.result", id: f.id, error: `unknown tool: ${f.name}` });
			return;
		}

		const ctl = new AbortController();
		this.runningTools.set(f.id, ctl);
		void (async () => {
			try {
				const result = await tool.handler(f.args, ctl.signal);
				// A cancelled job is gone server-side; a late answer would be
				// dropped there anyway (SPEC §9), so say nothing.
				if (!ctl.signal.aborted) this.send({ t: "tool.result", id: f.id, result });
			} catch (err) {
				if (!ctl.signal.aborted) {
					this.send({ t: "tool.result", id: f.id, error: String(err) });
				}
			} finally {
				this.runningTools.delete(f.id);
			}
		})();
	}

	/** Aborts a running handler. The id stays served: a replay must not re-run it. */
	private abortJob(id: string): void {
		const ctl = this.runningTools.get(id);
		if (!ctl) return;
		this.runningTools.delete(id);
		ctl.abort();
	}

	/**
	 * Aborts every running handler: the session that dispatched them is gone.
	 * A dropped control socket is *not* one of those moments — the session is
	 * parked with its jobs alive (SPEC §13), and a handler that finishes during
	 * the gap answers on the re-attached socket.
	 */
	private abortJobs(): void {
		for (const ctl of this.runningTools.values()) ctl.abort();
		this.runningTools.clear();
	}

	/**
	 * Folds one `transcript.tool` frame into `history` and reports it. The log
	 * covers every binding, not just this client's tools, and a reconnect
	 * replays it, so each job contributes at most one `tool_call` record and
	 * one `tool_result` record. A result for a job whose `started` was never
	 * seen is kept: the history handed back on resume must not lose a job this
	 * client only learned about late.
	 */
	private recordTool(f: TranscriptTool): void {
		const ts = f.ts || Date.now();
		if (f.event === "started") {
			if (this.toolCalls.has(f.jobId)) return;
			this.toolCalls.add(f.jobId);
			const m: ToolCallMessage = { role: "tool_call", jobId: f.jobId, name: f.name, ts };
			if (f.args !== undefined) m.args = f.args;
			if (f.round !== undefined) m.round = f.round;
			if (f.body !== undefined) m.body = f.body;
			this.history.push(m);
		} else {
			if (this.toolResults.has(f.jobId)) return;
			this.toolResults.add(f.jobId);
			const m: ToolResultMessage = {
				role: "tool_result",
				jobId: f.jobId,
				name: f.name,
				event: f.event,
				ts,
			};
			if (f.result !== undefined) m.result = f.result;
			if (f.error) m.error = f.error;
			this.history.push(m);
		}
		this.opts.onTranscript({
			role: "tool",
			jobId: f.jobId,
			name: f.name,
			event: f.event,
			...(f.args !== undefined ? { args: f.args } : {}),
			...(f.result !== undefined ? { result: f.result } : {}),
			...(f.error ? { error: f.error } : {}),
			ts,
		});
	}

	/**
	 * Folds one backend note (`transcript.context`, SPEC §18.8) into
	 * `history` and reports it. A replay hands back a note we already hold, so
	 * the row is keyed by `messageId` and added once; `onTranscript` still
	 * fires, as it does for a replayed final.
	 */
	private recordContext(f: TranscriptContext): void {
		const ts = f.ts || Date.now();
		const fresh = !this.contextRows.has(f.messageId);
		this.contextRows.add(f.messageId);
		if (f.source === "agent") {
			const { text, kind, mode } = f;
			if (fresh) this.history.push({ role: "context", text, source: "agent", kind, mode, ts });
			this.opts.onTranscript({ role: "context", messageId: f.messageId, text, source: "agent", kind, mode, ts });
			return;
		}
		if (fresh) {
			const m: SpeechMessage = { role: "context", text: f.text, source: "backend", ts };
			if (f.jobId) m.jobId = f.jobId;
			this.history.push(m);
		}
		this.opts.onTranscript({
			role: "context",
			messageId: f.messageId,
			text: f.text,
			source: "backend",
			mode: f.mode,
			...(f.jobId ? { jobId: f.jobId } : {}),
			ts,
		});
	}

	private setState(s: Status): void {
		if (this.state === s) return;
		if (this.phase === "closed" && s !== "closed") return;
		this.state = s;
		this.telemetry?.record("state", { state: s });
		// The liaison is speaking, which is the only window where playout is
		// worth polling for. The poll stops itself once the burst it was
		// following has ended (`samplePlayout`).
		if (s === "speaking") this.startPlayoutPoll();
		this.opts.onState(s);
	}

	// ------------------------------------------------------------- flight recorder

	/**
	 * Starts the client half of the trace (SPEC §12), once the session is
	 * live. Before that there is no bound session to write to and a batch would
	 * only be dropped and counted; after a suspend this is what opens the
	 * collector again, on the new session's trace. Idempotent: a re-attach runs
	 * the same handshake tail on a collector that never stopped.
	 */
	private startTelemetry(): void {
		const tele = this.telemetry;
		if (!tele || this.teleTimer !== null) return;
		// Reopened after a suspend closed it; a no-op on a re-attach, which
		// never closed it, and on the first handshake.
		tele.open();
		// The offset between the two clocks is what every later `atMs` is read
		// against, so it is established before anything else is observed.
		tele.record("clock", { clientMs: Date.now() });
		this.teleTicks = 0;
		this.teleTimer = setInterval(() => this.telemetryTick(), TELEMETRY_LEVEL_MS);
	}

	/**
	 * Stops the clocks. `final` is a teardown: the last batch is flushed while
	 * the socket is still there and the collector is closed for good, because a
	 * session that has ended has nothing left to observe and a live timer on it
	 * is a leak. A suspend is not final — but it is the end of this session's
	 * trace, so what is held is dropped rather than flushed at a node that has
	 * stopped listening, and the next handshake reopens the collector for the
	 * next session's file.
	 */
	private stopTelemetry(final: boolean): void {
		this.statsGeneration++;
		this.statsBusy = false;
		this.playoutBusy = false;
		if (this.teleTimer !== null) {
			clearInterval(this.teleTimer);
			this.teleTimer = null;
		}
		this.stopPlayoutPoll();
		// A burst cut off by the teardown ended, whatever the poll was about to
		// say: a trace with a start and no end reads as playout that never
		// stopped.
		if (this.playingOut) this.playoutEdge(false, final ? "closed" : "suspended", undefined, undefined, final);
		// The part-built second goes with the clocks. A window carried across a
		// suspend would report the pause's min and max as one second of level,
		// on the far side of a gap of any length.
		this.levelN = 0;
		const tele = this.telemetry;
		if (!tele) return;
		if (final) tele.stop();
		else tele.close();
	}

	/**
	 * One second of the recorder's own clock: the level window if the graph
	 * measured anything, and every fifth tick the clock and the transport
	 * counters.
	 */
	private telemetryTick(): void {
		const tele = this.telemetry;
		if (!tele) return;
		if (this.levelN > 0) {
			tele.record("level", {
				minDb: this.levelMinDb,
				maxDb: this.levelMaxDb,
				peakDb: this.levelPeakDb,
				floorDb: this.levelFloorDb,
				n: this.levelN,
				muted: this.isMuted,
			});
			this.levelN = 0;
		}
		this.teleTicks++;
		if ((this.teleTicks * TELEMETRY_LEVEL_MS) % TELEMETRY_TICK_MS !== 0) return;
		tele.record("clock", { clientMs: Date.now() });
		this.sampleStats();
	}

	/** Transport counters, every `TELEMETRY_TICK_MS`. */
	private sampleStats(): void {
		const pc = this.pc;
		if (!this.telemetry || !pc || typeof pc.getStats !== "function" || this.statsBusy) return;
		const generation = this.statsGeneration;
		this.statsBusy = true;
		void pc
			.getStats()
			.then(
				(report) => {
					if (generation === this.statsGeneration && pc === this.pc) this.recordStats(report);
				},
				() => {
					/* a leg torn down mid-poll: the next tick asks again */
				},
			)
			.finally(() => {
				if (generation === this.statsGeneration) this.statsBusy = false;
			});
	}

	/**
	 * Folds one stats report into a `stats` event: what the inbound audio
	 * lost, concealed and jittered, what the uplink sent, and the round trip
	 * of the pair actually carrying it. Cumulative, not deltas — the trace has
	 * every sample, so a reader can difference them and cannot be misled by a
	 * counter that reset under a renegotiation.
	 *
	 * The jitter-buffer block is the client's half of every latency argument
	 * this project has. `jitterBufferDelay / jitterBufferEmittedCount` is the
	 * mean seconds each emitted sample waited — the audio held in the browser
	 * at the moment of a barge-in, which a trace could previously only infer
	 * from the gap between the server's `speaking` edge and the energy poll's.
	 * `jitterBufferTargetDelay` and `jitterBufferMinimumDelay` say what NetEq
	 * is *aiming* at, which is what `ConnectOptions.jitterBufferTarget` moves;
	 * the inserted/removed sample counters are what it paid to get there
	 * (time-stretching), and the conceal counters what it paid when it could
	 * not.
	 */
	private recordStats(report: RTCStatsReport): void {
		const fields: Record<string, unknown> = {};
		report.forEach((entry) => {
			const stat = entry as Record<string, unknown>;
			const kind = stat["kind"];
			switch (stat["type"]) {
				case "inbound-rtp": {
					if (kind !== "audio") break;
					fields["packetsReceived"] = statNum(stat, "packetsReceived");
					fields["packetsLost"] = statNum(stat, "packetsLost");
					fields["jitter"] = statNum(stat, "jitter");
					fields["concealedSamples"] = statNum(stat, "concealedSamples");
					fields["silentConcealedSamples"] = statNum(stat, "silentConcealedSamples");
					fields["concealmentEvents"] = statNum(stat, "concealmentEvents");
					fields["totalSamplesReceived"] = statNum(stat, "totalSamplesReceived");
					fields["jitterBufferDelay"] = statNum(stat, "jitterBufferDelay");
					fields["jitterBufferEmittedCount"] = statNum(stat, "jitterBufferEmittedCount");
					fields["jitterBufferTargetDelay"] = statNum(stat, "jitterBufferTargetDelay");
					fields["jitterBufferMinimumDelay"] = statNum(stat, "jitterBufferMinimumDelay");
					fields["insertedSamplesForDeceleration"] = statNum(
						stat,
						"insertedSamplesForDeceleration",
					);
					fields["removedSamplesForAcceleration"] = statNum(stat, "removedSamplesForAcceleration");
					break;
				}
				case "outbound-rtp": {
					if (kind !== "audio") break;
					fields["packetsSent"] = statNum(stat, "packetsSent");
					break;
				}
				case "candidate-pair": {
					// The nominated succeeded pair is the one carrying media;
					// every other pair's rtt describes a path nothing is on.
					if (stat["state"] !== "succeeded" || stat["nominated"] !== true) break;
					const rtt = statNum(stat, "currentRoundTripTime");
					if (rtt !== undefined) fields["rttMs"] = Math.round(rtt * 1000);
					break;
				}
				default:
					break;
			}
		});
		this.telemetry?.record("stats", fields);
	}

	/** Arms the playout poll, for as long as there may be a burst to follow. */
	private startPlayoutPoll(): void {
		this.playoutTail = PLAYOUT_TAIL_POLLS;
		if (this.playoutTimer !== null) return;
		this.playoutTimer = setInterval(() => this.samplePlayout(), TELEMETRY_PLAYOUT_MS);
	}

	private stopPlayoutPoll(): void {
		if (this.playoutTimer === null) return;
		clearInterval(this.playoutTimer);
		this.playoutTimer = null;
		this.quietPolls = 0;
		this.playoutTail = 0;
		this.lastEnergy = null;
	}

	/**
	 * One playout sample. Runs at `TELEMETRY_PLAYOUT_MS` while the liaison is
	 * speaking and for as long afterwards as the burst is still audible — the
	 * client's own buffer is exactly the gap between the server's playout
	 * position and this, which is the number a barge-in argument turns on.
	 */
	private samplePlayout(): void {
		const pc = this.pc;
		if (!pc || typeof pc.getStats !== "function") {
			this.stopPlayoutPoll();
			return;
		}
		// `speaking` re-arms the tail, and so does every audible sample
		// (`foldPlayout`) — that is how a burst still draining out of the
		// client's buffer holds the poll open past the state. Anything else
		// spends it, *including* an open burst: "open" is only what the last
		// sample said, and a report that stops carrying an energy delta at all
		// — a leg torn down under a renegotiation, a receiver whose counters
		// stalled — would otherwise leave this interval running at
		// `TELEMETRY_PLAYOUT_MS` for the rest of the session, on a burst that
		// never ends.
		if (this.state === "speaking") this.playoutTail = PLAYOUT_TAIL_POLLS;
		else if (this.playoutTail-- <= 0) {
			if (this.playingOut) this.playoutEdge(false, "stalled", undefined, this.outputAudibleAt);
			this.stopPlayoutPoll();
			return;
		}
		if (this.playoutBusy) return;
		const generation = this.statsGeneration;
		this.playoutBusy = true;
		void pc
			.getStats()
			.then(
				(report) => {
					if (generation !== this.statsGeneration || pc !== this.pc) return;
					// The report is already in hand, and 5 Hz is the resolution
					// a barge-in is argued at: the 5 s tick cannot say how deep
					// the jitter buffer was at the cut, and this poll runs
					// exactly across the window where that matters — while the
					// liaison speaks, and through the tail while the buffer
					// drains.
					if (this.telemetry) this.recordStats(report);
					this.foldPlayout(report);
					this.reportPlayoutPosition();
				},
				() => {
					/* the leg went away; the guard above stops the poll */
				},
			)
			.finally(() => {
				if (generation === this.statsGeneration) this.playoutBusy = false;
			});
	}

	/**
	 * Turns one stats report into a playout level and, if it crossed, an edge.
	 *
	 * `totalAudioEnergy` is a running sum of squared sample amplitudes and
	 * `totalSamplesDuration` the seconds behind it, so their deltas are the
	 * mean square of what was decoded in this interval: the only field in
	 * `getStats` that distinguishes speech from the silence this server keeps
	 * sending between turns. `audioLevel` is the fallback for a browser that
	 * reports the instantaneous value and not the totals.
	 */
	private foldPlayout(report: RTCStatsReport): void {
		let energy: number | undefined;
		let dur: number | undefined;
		let level: number | undefined;
		report.forEach((entry) => {
			const stat = entry as Record<string, unknown>;
			if (stat["type"] !== "inbound-rtp" || stat["kind"] !== "audio") return;
			energy = statNum(stat, "totalAudioEnergy");
			dur = statNum(stat, "totalSamplesDuration");
			level = statNum(stat, "audioLevel");
			const jbDelay = statNum(stat, "jitterBufferDelay");
			const jbCount = statNum(stat, "jitterBufferEmittedCount");
			if (jbDelay !== undefined && jbCount) {
				const prev = this.lastJitter;
				this.lastJitter = { delay: jbDelay, count: jbCount };
				// A count that did not advance (or went back, on a new receiver)
				// has no interval to average; the cumulative mean stands in.
				this.jitterMs =
					prev && jbCount > prev.count
						? (1000 * (jbDelay - prev.delay)) / (jbCount - prev.count)
						: (1000 * jbDelay) / jbCount;
			}
		});
		let db: number | null = null;
		if (energy !== undefined && dur !== undefined) {
			const prev = this.lastEnergy;
			this.lastEnergy = { energy, dur };
			if (prev && dur > prev.dur) {
				const meanSquare = Math.max(0, energy - prev.energy) / (dur - prev.dur);
				db = meanSquare > 0 ? 10 * Math.log10(meanSquare) : -100;
			}
		} else if (level !== undefined) {
			db = level > 0 ? 20 * Math.log10(level) : -100;
		}
		if (db === null) return;
		const rounded = Math.round(db * 10) / 10;
		if (db > this.playoutOnDb) {
			this.quietPolls = 0;
			// Audible is its own reason to keep polling: the server leaves
			// `speaking` when it has finished *producing* the turn and the
			// client's buffer is still playing it out.
			this.playoutTail = PLAYOUT_TAIL_POLLS;
			this.playoutEdge(true, "stats", rounded);
			const clock = this.outputClockSamples();
			if (clock !== null) this.outputAudibleAt = Math.max(this.outputAudibleAt ?? clock, clock);
			return;
		}
		if (!this.playingOut) return;
		this.quietPolls++;
		if (this.quietPolls < PLAYOUT_QUIET_POLLS) return;
		this.playoutEdge(false, "stats", rounded, this.outputAudibleAt);
	}

	/** The hardware output clock in the protocol's 24 kHz units. */
	private outputClockSamples(): number | null {
		const ctx = this.outCtx;
		if (!ctx || ctx.state !== "running" || typeof ctx.getOutputTimestamp !== "function") return null;
		const stamp = ctx.getOutputTimestamp();
		const contextTime = stamp.contextTime;
		if (contextTime === undefined || !Number.isFinite(contextTime) || contextTime < 0) return null;
		return Math.floor(contextTime * 24_000);
	}

	/**
	 * Reports only a current audible position. The accumulated clock excludes
	 * silence between bursts and time spent ducked, matching the server
	 * transport's source clock. A baseline is not evidence that anything was
	 * heard: the first authoritative report waits for positive progress.
	 */
	private reportPlayoutPosition(force = false): void {
		if (!this.playingOut || this.ducked) return;
		const clock = this.outputClockSamples();
		if (clock === null) return;
		if (this.outputBurstAt === null) this.outputBurstAt = clock;
		// Quiet or unusable polls are not audible progress. Pin reports to the
		// greatest output timestamp observed while audible. Hardware clocks can
		// briefly regress across a device or route transition; that must neither
		// rewind this burst nor prevent a later forward timestamp from resuming
		// reports.
		const through = this.outputAudibleAt ?? clock;
		const playedSamples = this.outputPlayedSamples + Math.max(0, through - this.outputBurstAt);
		if (
			playedSamples <= 0 ||
			(this.outputLastReportedSamples !== null && playedSamples <= this.outputLastReportedSamples)
		)
			return;
		const now = Date.now();
		if (!force && now - this.outputReportedAt < 500) return;
		if (this.sendPlayoutPosition(playedSamples)) {
			this.outputLastReportedSamples = playedSamples;
			this.outputReportedAt = now;
		}
	}

	private finishPlayoutClock(through?: number | null, emit = true): void {
		if (this.outputBurstAt === null) {
			this.outputAudibleAt = null;
			return;
		}
		const live = through === undefined ? this.outputClockSamples() : through;
		const clock =
			through === undefined && live !== null
				? Math.max(this.outputAudibleAt ?? live, live)
				: live;
		if (clock !== null) {
			const accrued = this.outputPlayedSamples + Math.max(0, clock - this.outputBurstAt);
			this.outputPlayedSamples = Math.max(accrued, this.outputLastReportedSamples ?? 0);
			if (
				emit &&
				this.outputPlayedSamples > 0 &&
				(this.outputLastReportedSamples === null ||
					this.outputPlayedSamples > this.outputLastReportedSamples)
			) {
				if (this.sendPlayoutPosition(this.outputPlayedSamples)) {
					this.outputLastReportedSamples = this.outputPlayedSamples;
					this.outputReportedAt = Date.now();
				}
			}
		}
		this.outputBurstAt = null;
		this.outputAudibleAt = null;
	}

	/**
	 * One playout edge, deduplicated across everything that can see one: a
	 * burst is exactly one `playout_start` and one `playout_end`, and `via`
	 * records what closed it — `"stats"` the energy poll, `"track"` the remote
	 * track's mute, `"stalled"` a poll that ran out of readings, `"closed"` or
	 * `"suspended"` a teardown that took the burst with it. Only the energy
	 * poll ever opens one; RTP arriving is not the same thing as audio.
	 */
	private playoutEdge(
		on: boolean,
		via: string,
		db?: number,
		through?: number | null,
		emitPosition = true,
	): void {
		if (on === this.playingOut) return;
		if (!on) this.finishPlayoutClock(through, emitPosition);
		this.playingOut = on;
		this.telemetry?.record(on ? "playout_start" : "playout_end", {
			via,
			...(db === undefined ? {} : { db }),
		});
		if (on) {
			this.outputBurstAt = this.outputClockSamples();
			this.outputAudibleAt = this.outputBurstAt;
			this.reportPlayoutPosition(true);
			this.startPlayoutPoll();
		} else this.quietPolls = 0;
	}

	/** A device change, named where the last enumeration gave it a label. */
	private noteDevice(role: "input" | "output", deviceId: string | null): void {
		this.telemetry?.record("device", {
			role,
			deviceId,
			...(deviceId ? { label: this.deviceLabels.get(deviceId) ?? "" } : {}),
		});
	}

	/**
	 * Every error this SDK reports, recorded before it is handed on: the
	 * consumer's handler may throw, navigate or tear the page down, and an
	 * error missing from the trace is the one that explains the session.
	 */
	private reportError(e: ErrorFrame): void {
		this.telemetry?.record("error", {
			code: e.code,
			message: e.message,
			fatal: e.fatal === true,
		});
		this.opts.onError?.(e);
	}

	// ------------------------------------------------------------ wake on speech

	/**
	 * Builds the graph of `docs/wake-on-speech.md` §5, once per session:
	 * capture → source → `liaison-wake` worklet → destination node, whose
	 * track is what the PeerConnection sends. With `wake` on it is kept across
	 * an idle suspend, because a detector on the audio render thread is the
	 * only thing that keeps hearing the room while nothing is connected — and
	 * the retained capture is also what exempts the page from being frozen.
	 *
	 * The same graph serves the input meter, for a different reason: the main
	 * thread never sees samples, so a level has to be measured where they are.
	 * A monitoring-only session builds this and nothing else follows from it —
	 * no tuning, no arming, no retention.
	 *
	 * This is the only place a graph is born, which makes it the only place the
	 * mute gate and the meter's cadence have to be established: every later
	 * change to either goes through `applyMute` or a fresh graph.
	 *
	 * The processor is a second served bundle, resolved against whichever
	 * prefix the consumer imported this module from: a `blob:` URL would be
	 * refused by WebKit's `addModule` and by any strict `script-src`.
	 */
	private async buildAudioGraph(mic: MediaStream): Promise<AudioGraph> {
		const ctx = new AudioContext({ sampleRate: 48_000, latencyHint: "interactive" });
		try {
			await ctx.audioWorklet.addModule(new URL("liaison-wake.js", import.meta.url), {
				credentials: "omit",
			});
			const node = new AudioWorkletNode(ctx, "liaison-wake", {
				numberOfInputs: 1,
				numberOfOutputs: 1,
				outputChannelCount: [1],
			});
			const src = ctx.createMediaStreamSource(mic);
			// Mono, explicitly: the spec default for a destination node is 2, so
			// the factory form would hand the PeerConnection a stereo track
			// where the microphone reports one channel and the worklet is
			// deliberately `outputChannelCount: [1]`. It costs nothing on the
			// wire (`stereo=0` is negotiated either way) and an uplink whose
			// channel count disagrees with everything feeding it is a thing
			// somebody would have to explain later.
			const dest = new MediaStreamAudioDestinationNode(ctx, { channelCount: 1 });
			src.connect(node).connect(dest);
			// `muted: false` is not an assumption, it is what a worklet that has
			// been told nothing has: the gate starts open. `applyMute` runs on
			// every leg, including this one, before a PeerConnection exists to
			// carry a sample anywhere.
			const graph: AudioGraph = { ctx, node, src, dest, muted: false };
			node.port.onmessage = (ev: MessageEvent) => {
				this.onWakeReport(ev.data as WakeReport);
			};
			const tuning = this.wake;
			// Only a wake session tunes the detector. A monitoring-only one
			// leaves the worklet on its defaults, which it never acts on: it is
			// never armed, so the onset rule and the ring are inert and the
			// ring's buffers are the one thing worth not allocating — hence no
			// `tune`, whose `preRollMs` is what sizes them.
			if (tuning) {
				postWake(graph, {
					type: "tune",
					sustainMs: tuning.sustainMs,
					marginDb: tuning.marginDb,
					preRollMs: tuning.preRollMs,
				});
			}
			// The meter's cadence, and only when a consumer asked for one: off is
			// the worklet's own default, and a session with no `onLevel` should
			// leave the render thread accumulating nothing and saying nothing.
			if (this.opts.onLevel) {
				postWake(graph, { type: "level", intervalMs: this.levelIntervalMs });
			}
			// Live from the first quantum: while a leg is up the worklet is a
			// pass-through and holds no ring.
			postWake(graph, { type: "live" });
			if (ctx.state === "suspended") await ctx.resume();
			return graph;
		} catch (err) {
			void ctx.close().catch(() => {
				/* nothing to keep: the graph never came up */
			});
			throw err;
		}
	}

	/**
	 * Wakes the context a live leg is about to be built on. A platform can
	 * suspend an `AudioContext` under us — mobile Safari does it when the tab
	 * is hidden — and a suspended context is a worklet that has stopped, whose
	 * destination node would carry exact zeros into the leg: `TODO.md` 29
	 * manufactured on purpose. One that will not resume is therefore not
	 * adoptable, so the whole graph goes and the caller acquires a fresh
	 * capture instead of bringing up a deaf session.
	 */
	private async adoptWakeContext(): Promise<void> {
		const graph = this.graph;
		if (!graph || graph.ctx.state !== "suspended") return;
		try {
			await graph.ctx.resume();
		} catch (err) {
			this.releaseCapture();
			this.graphFailed("the audio context would not resume", err);
		}
	}

	/**
	 * Wakes a context the platform put to sleep under a live graph. Refusal is
	 * deliberately *not* fatal to the graph here: some browsers refuse
	 * `resume()` outside a user gesture, and dropping the graph would throw
	 * away the thing the next chance needs. So the refusal is reported — once
	 * per episode, because a browser that wants a gesture refuses every
	 * automatic attempt and the consumer needs telling once, not on every tab
	 * flip — and the watch stays up for the next `statechange` or for the tab
	 * coming back. A graph that cannot run is observable; what it must never be
	 * is silence.
	 *
	 * The code says what was actually lost. Armed, this is the detector: speech
	 * cannot wake the session (`wake.failed`, which `docs/sdk-web.md` §7
	 * documents). On a live leg it is worse and quieter — the destination track
	 * is feeding the PeerConnection exact zeros, which is `TODO.md` 29's
	 * condition, and the only local symptom is `inputLevel` going stale.
	 */
	private async resumeContext(): Promise<void> {
		const graph = this.graph;
		if (!graph || graph.ctx.state !== "suspended") return;
		try {
			await graph.ctx.resume();
			this.ctxDeaf = false;
		} catch (err) {
			if (this.ctxDeaf) return;
			this.ctxDeaf = true;
			if (this.armed) {
				this.mediaFailed(
					"wake.failed",
					"the audio context will not resume, so speech cannot wake this session until the page is interacted with",
					err,
				);
				return;
			}
			// A graph is also retained, unarmed, through an `"error"` suspend
			// waiting on its auto-resume: that session is not sending anything
			// at all, and telling a consumer it is "sending silence" would be
			// simply untrue. What it has lost is the graph the resume is about
			// to re-offer.
			this.mediaFailed(
				"audio.failed",
				this.phase === "suspended"
					? "the audio context will not resume, so this session cannot bring its microphone back until the page is interacted with"
					: "the audio context will not resume, so this session is sending silence until the page is interacted with",
				err,
			);
		}
	}

	/**
	 * Watches the context for as long as the graph exists — not merely for as
	 * long as the detector is armed.
	 *
	 * Both cases are the same platform behaviour seen from two ends. Arming
	 * happens at the idle suspend and the tab is hidden hours later, so an
	 * arm-time check alone would never see the overnight case; and a context
	 * suspended under a *live* leg stops the worklet, so the destination node
	 * carries exact zeros to the server for the rest of the call with nothing
	 * to resume it. That second case used to belong to `wake` sessions only,
	 * where the graph was; input level monitoring hands the same graph to any
	 * session that draws a meter, and a meter whose only symptom is a stale
	 * `inputLevel` is not a symptom anybody reads.
	 *
	 * `visibilitychange` is the second chance, for a browser that will not
	 * resume a hidden tab's context at all.
	 */
	private watchAudioContext(graph: AudioGraph): void {
		if (this.ctxWatch) return;
		const check = (): void => {
			// A stale listener, or one firing on the way down through
			// `releaseCapture`, has nothing to fix.
			if (this.graph !== graph) return;
			void this.resumeContext();
		};
		const onVisible = (): void => {
			if (document.visibilityState === "visible") check();
		};
		graph.ctx.addEventListener("statechange", check);
		document.addEventListener("visibilitychange", onVisible);
		this.ctxWatch = () => {
			graph.ctx.removeEventListener("statechange", check);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}

	/**
	 * Drops the watch, which happens exactly where the graph does
	 * (`releaseCapture`). The report latch lives exactly as long as the watch,
	 * so the next graph gets to speak up again.
	 */
	private unwatchAudioContext(): void {
		this.ctxDeaf = false;
		const drop = this.ctxWatch;
		if (!drop) return;
		this.ctxWatch = null;
		drop();
	}

	/**
	 * Arms the detector after an idle suspend: the worklet stops passing audio
	 * through, starts filling the bounded pre-roll ring and reports the first
	 * onset it is sure of.
	 *
	 * `"idle"`-only, and never during `"suspending"` (§8). Not `"client"`: the
	 * user asked for that pause and only an explicit `resume()` may undo it.
	 * Not `"error"`: that one already resumes itself, and arming would race
	 * the auto-resume that runs inside `enterSuspended`.
	 */
	private armWake(): void {
		if (!this.wake || !this.graph || this.armed) return;
		if (this.phase !== "suspended" || this.suspendReason !== "idle") return;
		this.armed = true;
		postWake(this.graph, { type: "armed" });
		// The watch belongs to the graph and is already up; this call is
		// idempotent and kept because arming is also the moment to ask a
		// context the page may have backgrounded on its way here to come back —
		// an armed worklet on a suspended context hears nothing at all.
		this.watchAudioContext(this.graph);
		void this.resumeContext();
	}

	/**
	 * One onset while armed, which is the whole trigger: speech is the user
	 * action that undoes an idle suspend (§8). It calls the public `resume()`
	 * and inherits its join, so a wake racing a user's tap is one handshake.
	 *
	 * Bounded twice. `maxWakesPerMinute` bounds a noisy room — a false wake
	 * costs a token mint, a handshake, a Deepgram dial and a whole fresh idle
	 * window of open session — and a wake whose session commits no turn buys
	 * an exponential backoff, without which one failing token mint turns that
	 * room into an all-night retry loop against the customer's endpoint.
	 *
	 * And bounded by mute. A muted session is one the user has told to stop
	 * listening, so an onset while muted is ignored outright: no resume, no
	 * wake counted against the minute, no backoff charged — the detector does
	 * not even disarm, because the moment the user unmutes, speech should wake
	 * the session again with no further call. The worklet keeps reporting
	 * onsets while muted on purpose: it gates the output, not its ears, and
	 * this is the one place that distinction is decided.
	 */
	private onWakeOnset(report: { floorDb: number; levelDb: number }): void {
		const tuning = this.wake;
		if (!tuning || !this.armed || this.phase !== "suspended") return;
		if (this.isMuted) return;
		const now = Date.now();
		if (now < this.wakeBlockedUntil) return;
		// The last minute only: an older wake is forgotten rather than counted
		// against the room forever.
		this.wakeTimes = this.wakeTimes.filter((t) => now - t < 60_000);
		if (this.wakeTimes.length >= tuning.maxWakesPerMinute) return;
		this.wakeTimes.push(now);
		this.woken = true;
		this.wakeCommitted = false;
		// Rides on this resume's `session.start` and nothing else: the server
		// silences the greeting on it, and a reconnect or a re-attach of the
		// woken session is not itself a wake.
		this.wakeStart = true;
		this.lastWake = { at: now, floorDb: report.floorDb, levelDb: report.levelDb };
		this.telemetry?.record("wake", {
			floorDb: report.floorDb,
			levelDb: report.levelDb,
			backlogMs: this.wakeBacklogMs,
		});
		this.opts.onWake?.();
		void this.resume().catch((err: unknown) => {
			// The mint failed, or the handshake did. `resume()` left the session
			// suspended with its history intact and the detector still armed, so
			// the backoff is the only thing stopping the next onset from
			// spending another one.
			this.wakeStart = false;
			this.wakeSettled();
			this.mediaFailed("wake.failed", "the resume this onset triggered failed", err);
		});
	}

	/**
	 * Judges the last wake, once. A woken session that committed a turn clears
	 * the backoff; one that committed nothing — no `transcript.user{final}`,
	 * which is all the SDK can observe of a turn — doubles it.
	 */
	private wakeSettled(): void {
		if (!this.woken) return;
		this.woken = false;
		if (this.wakeCommitted) {
			this.wakeMisses = 0;
			this.wakeBlockedUntil = 0;
			return;
		}
		this.wakeMisses++;
		const delay = Math.min(
			WAKE_BACKOFF_MAX_MS,
			WAKE_BACKOFF_BASE_MS * 2 ** (this.wakeMisses - 1),
		);
		this.wakeBlockedUntil = Date.now() + delay;
	}

	/**
	 * The media leg carries audio, so the worklet's silence is over. A woken
	 * leg pays the pre-roll ring out ahead of live audio and stays that far
	 * behind until the backlog drains (§6); a leg the user tapped back simply
	 * goes live, because the ring holds audio nobody is waiting for.
	 */
	private wakeConnected(): void {
		const graph = this.graph;
		if (!graph || !this.armed) return;
		this.armed = false;
		// The watch is deliberately *not* dropped here. It belongs to the graph
		// (`releaseCapture` drops it), and the leg this disarm hands over to is
		// exactly the one where a context suspended under the worklet would
		// feed the server exact zeros with nothing watching. What a disarm
		// clears is the arming, not the graph's own safety net.
		if (!this.woken) {
			postWake(graph, { type: "live" });
			return;
		}
		postWake(graph, { type: "payout" });
		this.draining = true;
	}

	/**
	 * The woken turn committed, so the backlog may start paying for itself out
	 * of the silence after it — but not yet. Inside the false-cut window,
	 * dropping silence pulls the user's next onset earlier in wall time and has
	 * the server count it as a false cut. Every commit re-closes the window,
	 * because every commit opens a new one server-side.
	 */
	private armDrain(): void {
		const graph = this.graph;
		if (!graph) return;
		postWake(graph, { type: "drain", allow: false });
		this.clearDrainTimer();
		this.drainTimer = setTimeout(() => {
			this.drainTimer = null;
			if (this.draining && this.graph) {
				postWake(this.graph, { type: "drain", allow: true });
			}
		}, FALSE_CUT_WINDOW_MS);
	}

	private clearDrainTimer(): void {
		if (!this.drainTimer) return;
		clearTimeout(this.drainTimer);
		this.drainTimer = null;
	}

	/**
	 * worklet → main (§5): the onset, the backlog's progress, and the meter's
	 * interval reports.
	 */
	private onWakeReport(report: WakeReport): void {
		switch (report.type) {
			case "onset":
				this.onWakeOnset(report);
				break;
			case "backlog":
				this.wakeBacklogMs = report.ms;
				break;
			case "drained":
				this.wakeBacklogMs = 0;
				this.draining = false;
				this.clearDrainTimer();
				break;
			case "level":
				// `muted` is stamped here and not on the render thread: the
				// worklet is told what to gate, it is not the authority on what
				// the session's state is. One object per report, which is the
				// cadence the consumer asked for, and it is both handed to
				// `onLevel` and left on `s.inputLevel` for a meter reading from
				// `requestAnimationFrame`.
				this.inputLevel = {
					db: report.db,
					peakDb: report.peakDb,
					floorDb: report.floorDb,
					muted: this.isMuted,
				};
				// One aggregate per second reaches the trace, not every report:
				// the cadence is the consumer's to choose and a 20 ms meter
				// would be 50 lines a second of a number that barely moves.
				// Folded into fields, so the trace costs this loop no object.
				if (this.telemetry) {
					if (this.levelN === 0) {
						this.levelMinDb = report.db;
						this.levelMaxDb = report.db;
						this.levelPeakDb = report.peakDb;
					} else {
						if (report.db < this.levelMinDb) this.levelMinDb = report.db;
						if (report.db > this.levelMaxDb) this.levelMaxDb = report.db;
						if (report.peakDb > this.levelPeakDb) this.levelPeakDb = report.peakDb;
					}
					this.levelFloorDb = report.floorDb;
					this.levelN++;
				}
				this.opts.onLevel?.(this.inputLevel);
				break;
		}
	}

	/**
	 * A detector, a meter or a device that did not work is reported, never
	 * silently accepted. Never fatal: everything else about the session still
	 * works — the user has lost the option of being heard without a tap, or a
	 * level meter, or the microphone they picked.
	 *
	 * Two codes, because a consumer acts on them differently and `wake.failed`
	 * is the one `docs/sdk-web.md` §7 documents: it stays exactly the detector's
	 * code, and everything this SDK's media controls can fail at is
	 * `audio.failed`. A session that never asked about wake must not be handed
	 * a wake error.
	 */
	private mediaFailed(
		code: "wake.failed" | "audio.failed",
		what: string,
		cause?: unknown,
	): void {
		const why = cause instanceof Error ? cause.message : cause === undefined ? "" : String(cause);
		const kind = code === "wake.failed" ? "wake" : "audio";
		this.reportError({
			t: "error",
			code,
			message: why ? `${kind}: ${what}: ${why}` : `${kind}: ${what}`,
			fatal: false,
		});
	}

	/**
	 * A graph-wide failure, under the code that names what was actually lost:
	 * a session with `wake` on loses the detector, a monitoring-only one loses
	 * only its meter.
	 */
	private graphFailed(what: string, cause?: unknown): void {
		this.mediaFailed(this.wake ? "wake.failed" : "audio.failed", what, cause);
	}

	// ---------------------------------------------------------------- teardown

	/**
	 * The session was released (SPEC §10). Media goes; the conversation stays,
	 * because `history` and the dedup sets are what `resume()` rebuilds from.
	 * The socket is left alone on purpose: the server keeps it open and
	 * released, so the next `session.start` can go out on it.
	 */
	private enterSuspended(reason: SuspendReason): void {
		if (this.phase === "closed") return;
		// Read by `armWake`: which half of the media survives, and whether the
		// detector may arm at all, is a function of this reason and nothing
		// else.
		this.suspendReason = reason;
		this.clearReconnect();
		// Ahead of the recorder stopping, so the edge is in the trace: the
		// session is released and no `playback.duck{on:false}` is ever coming.
		// `dropPeer` below would catch it anyway, silently.
		this.duck(false, "reset");
		// The recorder stops before anything else does, and before the consumer's
		// `onSuspended` can record anything of its own: the node has already
		// closed this session's trace, so there is nothing left to write to and
		// a frame sent anyway comes back as a `protocol_error`. `suspend()`
		// flushed the last batch while the session was still bound.
		this.stopTelemetry(false);
		// A `session.start` round in flight is over: the session it was building
		// has just been released. Abandoning it is what stops `negotiateMedia`
		// adopting a microphone that resolves after this point — a lit recording
		// indicator, a second PeerConnection and an `rtc.offer` sent after the
		// suspend — and what stops the round from reporting `phase = "live"`
		// under `state = "suspended"`, which made resume() a silent no-op.
		this.abandonHandshake(new Error("session suspended"));
		// The transport half always goes. The capture, the context and the
		// worklet survive an `"idle"` suspend — the detector has to keep hearing
		// the room — and an `"error"` one *whose auto-resume is actually going
		// to run*, because that resume wants the graph it is about to re-offer.
		// Everything else releases: a `"client"` suspend because the pause the
		// user asked for has to be a real pause and put the recording indicator
		// out (§8), and an `"error"` suspend with `autoReconnect` off because
		// nothing is coming to repair it and a microphone held open on a
		// session with no repair is that same broken promise.
		//
		// Retention is `wake`'s and only `wake`'s. A graph is not the test —
		// input level monitoring builds one too, and a meter is no reason to
		// hold a microphone open on a session the server has released: nothing
		// would be listening to it, the recording indicator would stay lit on a
		// paused session, and the page would stay exempt from freezing for a bar
		// nobody is watching. The graph still has to *exist* for the retention
		// to mean anything, because a wake session whose detector failed to
		// build has nothing to keep hearing the room with.
		this.dropPeer();
		const repairing = reason === "error" && this.autoReconnect;
		const retain = this.wake !== null && this.graph !== null && (reason === "idle" || repairing);
		if (!retain) this.releaseCapture();
		// The job tree died with the session server-side; a handler still
		// running here could only answer nobody.
		this.abortJobs();
		// Same for the frames held for a re-attach: the jobs they answer and the
		// turn they belong to are gone, and this session will not be re-attached.
		this.pending = [];
		this.jobs = [];
		this.agent = undefined;
		this.finishPlayoutClock();
		this.outputPlayedSamples = 0;
		this.outputReportedAt = 0;
		this.outputLastReportedSamples = null;
		this.phase = "suspended";
		this.setState("suspended");
		this.waitSuspended?.resolve();
		this.opts.onSuspended?.(reason);
		// A wake whose session is back here without having committed a turn was
		// a false one: count it and hold the next onset off for the backoff.
		this.wakeSettled();
		this.armWake();
		// SPEC §13: media died under a conversation nobody asked to pause, so
		// the SDK repairs it. An idle or client suspend is exactly the pause the
		// user asked for; idle waits for the gesture, or for the speech that
		// `armWake` is now listening for.
		if (repairing) {
			void this.resume().catch(() => {
				// Stays suspended, history intact; the user can still tap
				// resume. But the capture this suspend kept was kept *for* the
				// resume that just failed, and nothing else will ever release
				// it — unless a newer resume is already using it.
				if (!this.resuming) this.releaseCapture();
			});
		}
	}

	/**
	 * Drops the transport half of the session: PeerConnection, the queued
	 * candidates and the ICE-restart clock. The `<audio>` element and the
	 * output context survive with no stream in them — both are inert, and
	 * re-using what the browser has already let play keeps a resume out of the
	 * autoplay gate.
	 */
	private dropPeer(): void {
		this.statsGeneration++;
		this.statsBusy = false;
		this.playoutBusy = false;
		if (this.playingOut) this.playoutEdge(false, "closed");
		this.stopPlayoutPoll();
		const pc = this.pc;
		if (pc) {
			pc.onicecandidate = null;
			pc.ontrack = null;
			pc.onconnectionstatechange = null;
			pc.close();
			this.pc = null;
		}
		if (this.audio) this.audio.srcObject = null;
		this.outSrc?.disconnect();
		this.outSrc = null;
		// The leg that would have carried the un-duck is gone. A duck is a
		// property of a turn in flight, and there is no turn in flight after
		// this — leaving one on would hand the next leg a silent liaison.
		this.duck(false, "reset");
		this.pendingIce = [];
		this.clearIceTimer();
		this.iceAttempts = 0;
		this.restarting = false;
	}

	/**
	 * Releases the capture half: the microphone tracks and, when the session
	 * has a graph, the `AudioContext`, the worklet and the destination node.
	 * This is the call that puts the recording indicator out, so every path
	 * promising the user a real pause runs it — `"client"`, `end()` and
	 * `destroy()`.
	 */
	private releaseCapture(): void {
		const graph = this.graph;
		this.graph = null;
		this.armed = false;
		this.unwatchAudioContext();
		this.draining = false;
		this.wakeBacklogMs = 0;
		// The meter's last reading goes with the render thread that produced it:
		// a stale bar on a session that is no longer capturing is a lie about
		// the microphone, and null is the documented "no graph" value.
		this.inputLevel = null;
		this.clearDrainTimer();
		releaseAudioGraph(graph);
		for (const t of this.mic?.getTracks() ?? []) t.stop();
		this.mic = null;
	}

	/** Drops the control socket, optionally closing it first. */
	private detachSocket(close: boolean): void {
		const ws = this.ws;
		if (!ws) return;
		this.ws = null;
		ws.onopen = null;
		ws.onmessage = null;
		ws.onerror = null;
		ws.onclose = null;
		if (close && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
			ws.close(1000, "client end");
		}
	}

	private rejectWaiters(err: Error): void {
		this.waitReady?.reject(err);
		this.waitReady = null;
		this.waitAnswer?.reject(err);
		this.waitAnswer = null;
		this.abort?.reject(err);
	}

	/**
	 * Abandons the `session.start` round in flight, if any: rejects everything
	 * it could be waiting on and releases the join slot, so its caller reports
	 * now instead of waiting out the 15 s budget and a new round — the
	 * auto-resume after a `reason:"error"` suspend — starts clean rather than
	 * joining the round being thrown away. Clearing `abort` is also how the
	 * round itself learns it no longer owns the session (see `owns`).
	 */
	private abandonHandshake(err: Error): void {
		this.waitReady?.reject(err);
		this.waitReady = null;
		this.waitAnswer?.reject(err);
		this.waitAnswer = null;
		const abort = this.abort;
		if (abort) {
			this.abort = null;
			abort.reject(err);
		}
		this.inFlight = null;
	}


	/** Rejects anything still awaiting a frame, then tears the session down. */
	private fail(err: Error): void {
		this.rejectWaiters(err);
		this.destroy();
	}

	/** Ends the session for good. Every other teardown path is reversible; this one is not. */
	private destroy(): void {
		if (this.phase === "closed") {
			this.setState("closed");
			return;
		}

		// Before the socket goes: the last batch is the one that says how the
		// session ended, and it is worth nothing once there is nothing to write
		// it to. The closing state is stamped here rather than left to the
		// `setState` below, which runs after the flush — and after the socket —
		// so the trace would otherwise end on whatever happened before the end.
		this.telemetry?.record("state", { state: "closed" });
		this.stopTelemetry(true);
		globalThis.document?.removeEventListener("visibilitychange", this.onVisibility);
		this.phase = "closed";

		this.clearReconnect();
		this.detachSocket(true);
		// The `devicechange` listener is the session's, and a session that has
		// closed must not keep one on `navigator.mediaDevices`: it would hold
		// this object — its history, its callbacks, its whole graph of closures
		// — reachable for as long as the page lives, and would go on calling
		// `onDevices` for a session the consumer has finished with.
		navigator.mediaDevices?.removeEventListener?.("devicechange", this.onDeviceChange);
		// Both halves: nothing here is coming back, and the recording indicator
		// must not outlive the session that lit it.
		this.dropPeer();
		this.releaseCapture();
		// The output context is a render thread, and one held by a closed
		// session is the same leak as a listener: `dropPeer` already un-ducked,
		// so nothing here has a fade left to finish.
		this.releaseOutput();
		if (this.audio) {
			this.audio.remove();
			this.audio = null;
		}

		// Session end cancels every job (SPEC §9); the socket is gone, so a
		// handler that keeps running could only talk to nobody.
		this.abortJobs();
		this.jobs = [];
		this.agent = undefined;
		this.pending = [];

		const closed = new Error("session closed");
		this.waitReady?.reject(closed);
		this.waitReady = null;
		this.waitAnswer?.reject(closed);
		this.waitAnswer = null;
		this.waitSuspended?.reject(closed);
		this.waitSuspended = null;
		// Left in place, not cleared: a handshake step that has not been reached
		// yet (teardown between two awaits in handshake()) must fail fast, not
		// wait out the budget.
		this.abort?.reject(closed);
		this.setState("closed");
	}
}

/** Opens a session: control socket, `session.start`, mic, PeerConnection. */
export async function connect(opts: ConnectOptions): Promise<Session> {
	const s = new Session(opts);
	await s.start();
	return s;
}

/**
 * The machine's audio devices, split by kind.
 *
 * Labels are empty strings until the page holds a `getUserMedia` grant. That
 * is the browser's rule and not a gap here — device names are fingerprinting
 * surface, so they are withheld until the user has allowed the microphone at
 * least once — and there is nothing to work around: a picker drawn before the
 * first `connect()` shows ids, and re-enumerating after the grant (or from
 * `onDevices`) fills in the names.
 */
export async function devices(): Promise<AudioDevices> {
	const all = await navigator.mediaDevices.enumerateDevices();
	const inputs: AudioDeviceInfo[] = [];
	const outputs: AudioDeviceInfo[] = [];
	for (const d of all) {
		// `kind` also carries video devices on a page that has camera access;
		// this SDK has no use for them and a picker built from this list should
		// not have to filter them out.
		if (d.kind !== "audioinput" && d.kind !== "audiooutput") continue;
		const info: AudioDeviceInfo = { deviceId: d.deviceId, label: d.label, groupId: d.groupId };
		if (d.kind === "audioinput") inputs.push(info);
		else outputs.push(info);
	}
	return { inputs, outputs };
}

/**
 * Whether this browser can choose a playback device at all. Chromium
 * implements `setSinkId`; Safari and Firefox do not, and there is no polyfill
 * — routing output is an OS-level decision everywhere else. A UI should hide
 * its output picker on false rather than offer a control that can only fail.
 */
export function outputDeviceSupported(): boolean {
	return "setSinkId" in HTMLMediaElement.prototype;
}

export const Liaison = { connect, devices, outputDeviceSupported, VERSION };
export default Liaison;
